import React, { Component } from 'react';
import moment from 'moment';
import 'moment-timezone';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Spinner } from 'react-activity';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import Headers from './../Header';
import NavigationBar from './../Navigation';
import 'react-day-picker/lib/style.css';
import store from '../../../../sessionStorage';
import { PATH } from '../../../../utils/Constants';
import {
    getEducatorAvailablityDays,
    getEducatorSubject,
    getEducatorSubCategoryList,
    getAvailableStudentList,
    createClassRoomSession,
} from '../../../action/educator/AppointmentAction';
import { showNotification } from '../../../action/NotificationAction';
import { RESPONSE_CONSTANT } from '../../../../utils/ResponseConstant';
import {
    appointmentBook,
    getEducatorAvailability,
    getPhysicianAvailability,
    getVisitInformation,
    rescheduleSession,
} from '../../../action/Admin/CommonAction';
import { customStyles, formatMessage, validateEmail } from '../../../../utils/Utility';
import AppConfig from '../../../../utils/common/DomainConfiguration';
import LanguageContext, { LanguageProvider } from '../../../../utils/common/LanguageContext';

// Register Spanish locale for DatePicker
registerLocale('es', es);

/**
 * @package VirtuOwl
 * @type {React.ComponentClass}
 */

const withLanguage = (Component) => {
    return (props) => (
        <LanguageContext.Consumer>
            {({ t, language }) => <Component {...props} t={t} language={language} />}
        </LanguageContext.Consumer>
    );
};

const MAX_EMAILS = 10;

// Session language options. Labels are display-only; `value` is what the backend receives.
const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
];
// Default language used when the user does not pick one.
// Healthcare defaults to Spanish (the language feature is healthcare-only);
// core always defaults to English.
const DEFAULT_TRANSCRIPTION_LANGUAGE = AppConfig?.isHealthCare === true ? 'es' : 'en';
// Option pre-selected in the dropdown by default; user can change it.
const DEFAULT_SESSION_LANGUAGE =
    LANGUAGE_OPTIONS.find((option) => option.value === DEFAULT_TRANSCRIPTION_LANGUAGE) ||
    LANGUAGE_OPTIONS[0];

const isTrueValue = (value) =>
    value === true ||
    String(value || '')
        .trim()
        .toLowerCase() === 'true';

const normalizeSessionType = (sessionType = '') =>
    String(sessionType)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

const isConferenceSessionType = (sessionType = '') =>
    normalizeSessionType(sessionType).includes('conference');

const isInPersonSessionType = (sessionType = '') => {
    const normalized = normalizeSessionType(sessionType);
    return normalized.includes('in person') || normalized.includes('inperson');
};

const isGroupSessionType = (sessionType = '') => {
    const normalized = normalizeSessionType(sessionType);
    return (
        normalized.includes('group') ||
        normalized.includes('classroom') ||
        normalized.includes('class room') ||
        normalized.includes('broadcast')
    );
};

const VISIT_TYPE_STRING_MAP = {
    ONE_TO_ONE: 2,
    ONE_TO_ONE_SESSION: 2,
    CLASS_ROOM_SESSION: 4,
    CLASSROOM_SESSION: 4,
    GROUP_SESSION: 4,
    IN_PERSON: 6,
    IN_PERSON_SESSION: 6,
    CONFERENCE: 8,
    CONFERENCE_SESSION: 8,
};

const normalizeVisitTypeValue = (visitType) => {
    const numericVisitType = Number(visitType);
    if (Number.isFinite(numericVisitType) && numericVisitType > 0) {
        return numericVisitType;
    }

    const normalizedVisitType = String(visitType || '')
        .trim()
        .toUpperCase();

    return VISIT_TYPE_STRING_MAP[normalizedVisitType] || 0;
};

const getSessionUserId = (user = {}) =>
    String(
        user?.id ||
            user?.userId ||
            user?.studentId ||
            user?.ownerId ||
            user?.participantId ||
            user?.value ||
            ''
    ).trim();

const isSessionUserThirdParty = (user = {}) =>
    user?.thirdParty === true || user?.isThirdParty === true;

const isSessionUserOwner = (user = {}) => {
    const role = String(user?.role || '').toUpperCase();
    if (role === 'PATIENT') {
        return false;
    }
    const normalizedFlags = [
        user?.owner,
        user?.isOwner,
        user?.is_owner,
        user?.isTutor,
        user?.is_tutor,
        user?.isEducator,
        user?.is_educator,
        user?.educator,
        user?.teacher,
    ].map((flag) => {
        if (typeof flag === 'string') {
            const normalizedFlag = flag.trim().toLowerCase();
            return normalizedFlag === 'true' || normalizedFlag === '1';
        }
        return flag === true || flag === 1;
    });

    const hasOwnerIdentifier =
        !!String(user?.ownerId || '').trim() ||
        !!String(user?.educatorId || '').trim() ||
        (!!String(user?.tutorId || '').trim() &&
            String(user?.tutorId || '').trim() === getSessionUserId(user));

    return (
        normalizedFlags.some(Boolean) ||
        ['TUTOR', 'OWNER', 'EDUCATOR', 'TEACHER', 'PHYSICIAN', 'DOCTOR'].includes(role) ||
        hasOwnerIdentifier
    );
};

const getExternalUserEmail = (user = {}) => {
    if (!user || typeof user !== 'object') {
        return '';
    }

    return String(user?.email || user?.userEmail || user?.mail || user?.value || '')
        .trim()
        .toLowerCase();
};

const getExternalUserEmailsFromDetails = (users = []) =>
    Array.from(
        new Set(
            (Array.isArray(users) ? users : [])
                .map((user) => getExternalUserEmail(user))
                .filter(Boolean)
        )
    );

const toUniqueNormalizedEmails = (value) =>
    Array.from(
        new Set(
            (Array.isArray(value) ? value : value ? [value] : [])
                .map((email) => {
                    if (typeof email === 'object' && email !== null) {
                        return String(email.value || email.email || '')
                            .trim()
                            .toLowerCase();
                    }
                    return String(email || '')
                        .trim()
                        .toLowerCase();
                })
                .filter(Boolean)
        )
    );

const getOverlappingEmails = (left = [], right = []) => {
    const rightSet = new Set(toUniqueNormalizedEmails(right));
    return toUniqueNormalizedEmails(left).filter((email) => rightSet.has(email));
};

const normalizeReferenceFileSource = (value = '') => {
    const source = String(value || '').trim();
    if (!source) {
        return '';
    }
    if (/^https?:\/\//i.test(source)) {
        // Keep signed URLs untouched; re-encoding can invalidate SAS query signatures.
        return source;
    }
    return source;
};

const getReferenceFileSource = (file) => {
    if (typeof file === 'string') {
        return normalizeReferenceFileSource(file);
    }
    if (file && typeof file === 'object') {
        return normalizeReferenceFileSource(
            file.base64 || file.fileUrl || file.url || file.path || file.image || file.file || ''
        );
    }
    return '';
};

const getReferenceFileId = (file) => {
    if (!file || typeof file !== 'object') {
        return '';
    }
    return String(
        file.id ||
            file.fileId ||
            file.referenceFileId ||
            file.visitComplaintImageId ||
            file.materialImageId ||
            ''
    ).trim();
};

const getReferenceFileCompareKey = (source = '') => {
    let value = String(source || '').trim();
    if (!value) {
        return '';
    }

    for (let i = 0; i < 3; i++) {
        try {
            const decoded = decodeURIComponent(value);
            if (decoded === value) {
                break;
            }
            value = decoded;
        } catch (error) {
            break;
        }
    }

    const basePath = value.split('?')[0] || value;
    return basePath.toLowerCase();
};

const isPdfFileSource = (source = '', mimeType = '') => {
    const value = String(source || '').toLowerCase();
    const type = String(mimeType || '').toLowerCase();
    return (
        value.includes('data:application/pdf') ||
        /\.pdf($|[?#])/i.test(value) ||
        value.includes('application/pdf') ||
        type.includes('pdf')
    );
};

const isImageFileSource = (source = '', mimeType = '') => {
    const value = String(source || '').toLowerCase();
    const type = String(mimeType || '').toLowerCase();
    return (
        value.startsWith('data:image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg)($|[?#])/i.test(value) ||
        value.includes('image/') ||
        type.startsWith('image/')
    );
};

const getStudentOptionKey = (option = {}) => {
    const id = String(option?.id || option?.value || '').trim();
    if (!id) {
        return '';
    }
    return `${id}-${isTrueValue(option?.owner) ? 'owner' : 'student'}`;
};

const dedupeStudentOptions = (options = []) => {
    const seen = new Set();
    return (Array.isArray(options) ? options : []).filter((option) => {
        const key = getStudentOptionKey(option);
        if (!key || seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
};

const normalizeStudentLabel = (label = '') =>
    String(label || '')
        .trim()
        .toLowerCase();

const excludeSelectedStudentOptions = (options = [], selected = []) => {
    const selectedOptions = Array.isArray(selected) ? selected : selected ? [selected] : [];
    const selectedKeys = new Set(
        dedupeStudentOptions(selectedOptions)
            .map((option) => getStudentOptionKey(option))
            .filter(Boolean)
    );
    const selectedLabels = new Set(
        dedupeStudentOptions(selectedOptions)
            .map((option) => normalizeStudentLabel(option?.label))
            .filter(Boolean)
    );

    return dedupeStudentOptions(options).filter((option) => {
        const key = getStudentOptionKey(option);
        const label = normalizeStudentLabel(option?.label);
        if (selectedKeys.has(key)) {
            return false;
        }
        if (label && selectedLabels.has(label)) {
            return false;
        }
        return !!key;
    });
};

class CreateSession extends Component {
    static propTypes = {
        getEducatorAvailablityDays: PropTypes.func,
        getEducatorSubject: PropTypes.func,
    };
    constructor(props) {
        super(props);

        const routeState = props.location?.state;
        const initialRescheduleSession =
            routeState?.isReschedule && routeState?.rescheduleSession?.visitId
                ? routeState.rescheduleSession
                : null;
        const initialVisitType = normalizeVisitTypeValue(initialRescheduleSession?.visitType);
        const initialSessionType = normalizeSessionType(
            initialRescheduleSession?.sessionType || ''
        );
        const initialConference =
            initialVisitType === 8 || isConferenceSessionType(initialSessionType);
        const initialInPerson = initialVisitType === 6 || isInPersonSessionType(initialSessionType);
        const initialGroup = initialVisitType === 4 || isGroupSessionType(initialSessionType);

        this.state = {
            isLoading: false,
            mobileNav: false,
            isOneToOne: initialRescheduleSession
                ? !initialGroup && !initialConference && !initialInPerson
                : true,
            conference: initialRescheduleSession ? initialConference : false,
            subjectId: '',
            subCategoryId: '',
            sessionLanguage: DEFAULT_SESSION_LANGUAGE,
            date: '',
            studentId: [],
            studentIds: [],
            summary: '',
            uploadedImage: [],
            isImageUploaded: false,
            startDate: new Date(),
            subjectList: [],
            subCategoryList: [],
            timeSlots: [],
            studentList: [],
            timeApp: [],
            myValues: [],
            studentSearchValue: '',
            tutorAvailableDays: [],
            role: '',
            levelId:
                this.props.userDetails.associatedLevels &&
                this.props.userDetails.associatedLevels.length === 1
                    ? this.props.userDetails.associatedLevels[0]
                    : '',
            isStudentLoading: false,
            isSubjectLoading: false,
            isSubcategoryLoading: false,
            isLevelLoading: false,
            isFromStudent: false,
            isVirtuowl: true,
            isInPerson: initialRescheduleSession ? initialInPerson : false,
            selectedChannelEmail: '',
            invitePatient: false,
            invitePatientCachedStudentId: '',
            invitePatientCachedStudentIds: [],
            emails: [],
            currentEmail: '',
            isRescheduleMode: !!initialRescheduleSession,
            rescheduleVisitId: initialRescheduleSession?.visitId || null,
            rescheduleSessionData: initialRescheduleSession,
            hasAppliedReschedulePrefill: false,
            isReschedulePrefillPending: !!initialRescheduleSession,
            hasStartedSession: isTrueValue(initialRescheduleSession?.hasStartedSession),
            lockedPrefilledStudentKeys: [],
            lockedPrefilledEmails: [],
            lockedPrefilledReferenceFileKeys: [],
            conferenceParticipantRemovalTouched: false,
        };
    }

    //componentDidMount
    componentDidMount() {
        const searchParams = new URLSearchParams(window.location.search);
        const routeState = this.props.location?.state;

        if (routeState?.isReschedule && routeState?.rescheduleSession?.visitId) {
            this.fetchRescheduleSessionDetails(routeState.rescheduleSession.visitId);
        }

        if (searchParams.get('studentId')) {
            this.setState({
                studentId: {
                    label: searchParams.get('name'),
                    value: searchParams.get('studentId'),
                },
                isFromStudent: true,
            });
        }
        if (searchParams.get('role')) {
            this.setState({ role: searchParams.get('role') === 'TUTOR' });
        }
        if (searchParams.get('levelId')) {
            let obj = {
                label: searchParams.get('associatename'),
                value: searchParams.get('levelId'),
            };
            this.setState({ isFromStudent: true, levelId: obj });
        }
        this.getEducatorAvailablityDays();
        this.getEducatorSubject();
    }

    getTutorId = () =>
        this.props?.userDetails?.tutor?.id || store.get('userSession')?.tutor?.id || null;

    // Profile timezone (IANA) from GET api/v4/user, cached for page reloads before Redux is refilled.
    // Falls back to the browser zone (previous behaviour).
    getProfileTimezone = () => {
        const name = this.props?.userDetails?.timezone?.name;
        if (name && moment.tz.zone(name)) {
            if (store.get('userTimezone') !== name) {
                store.set('userTimezone', name);
            }
            return name;
        }
        const cached = store.get('userTimezone');
        return cached && moment.tz.zone(cached) ? cached : moment.tz.guess();
    };

    // Today in the profile timezone, as a calendar Date for the date picker
    getProfileToday = () =>
        moment(moment.tz(this.getProfileTimezone()).format('YYYY-MM-DD'), 'YYYY-MM-DD').toDate();

    // Get Educator Availability Days
    getEducatorAvailablityDays() {
        let educatorId = this.getTutorId();
        if (!educatorId) {
            return;
        }
        this.props.getEducatorAvailablityDays(educatorId, (response) => {
            if (response) {
                if (response.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                    this.setState({
                        tutorAvailableDays: response.response.availableDays,
                    });
                } else {
                    this.props.showNotification(response.message, RESPONSE_CONSTANT.WARNING);
                    this.setState({ tutorAvailableDays: [] });
                }
            }
        });
    }

    // Get Subject Details
    getEducatorSubject() {
        this.setState({ isLevelLoading: true, isSubjectLoading: true });
        let educatorId = this.getTutorId();
        if (!educatorId) {
            this.setState({ isLevelLoading: false, isSubjectLoading: false });
            return;
        }
        const values = {
            pageNumber: 1,
            itemsPerPage: 99999,
        };
        this.props.getEducatorSubject(educatorId, values, (response) => {
            this.setState({ isLevelLoading: false, isSubjectLoading: false });
            if (response) {
                if (response.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                    this.setState({ subjectList: response.response.data }, () => {
                        this.applyReschedulePrefill(response.response.data || []);
                    });
                } else {
                    this.props.showNotification(response.message, RESPONSE_CONSTANT.WARNING);
                    this.setState({ subjectList: [] });
                }
            }
        });
    }

    // Get SubCategory Details
    getEducatorSubCategoryList(subjectId, callback) {
        this.setState({ isSubcategoryLoading: true });
        const values = {
            pageNumber: 1,
            itemsPerPage: 99999,
        };
        // Call the getEducatorSubCategoryList API with the selected subject ID
        this.props.getEducatorSubCategoryList(subjectId, values, (response) => {
            this.setState({ isSubcategoryLoading: false });
            if (response) {
                if (response.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                    const subCategoryList = response.response.data || [];
                    this.setState({ subCategoryList }, () => {
                        if (typeof callback === 'function') {
                            callback(subCategoryList);
                        }
                    });
                } else {
                    this.props.showNotification(response.message, RESPONSE_CONSTANT.WARNING);
                    this.setState({ subCategoryList: [] });
                    if (typeof callback === 'function') {
                        callback([]);
                    }
                }
            }
        });
    }

    // Get Available Slots Details
    getAvailableSlots = (date, callback, options = {}) => {
        const tutorId = this.getTutorId();
        if (!tutorId) {
            if (typeof callback === 'function') {
                callback([]);
            }
            return;
        }

        store.set('slotIds', []);
        this.props.getEducatorAvailability(
            tutorId,
            moment(date).format('YYYY-MM-DD'),
            (response) => {
                if (response) {
                    if (response.code === 200 && response.status === 'success') {
                        this.setState({ timeSlots: response.response }, () => {
                            if (typeof callback === 'function') {
                                callback(response.response || []);
                            }
                        });
                    } else {
                        this.props.showNotification(response.message, RESPONSE_CONSTANT.WARNING);
                        this.setState({ timeSlots: [] }, () => {
                            if (typeof callback === 'function') {
                                callback([]);
                            }
                        });
                    }
                }
            },
            options
        );
    };

    isPrefilledValueLockEnabled = () =>
        this.state.isRescheduleMode &&
        this.state.hasAppliedReschedulePrefill &&
        this.state.hasStartedSession;

    mergeLockedPrefilledStudents = (selectedItems = []) => {
        const normalizedSelected = dedupeStudentOptions(
            Array.isArray(selectedItems) ? selectedItems : []
        );

        if (!this.isPrefilledValueLockEnabled()) {
            return normalizedSelected;
        }

        const lockedKeys = new Set(this.state.lockedPrefilledStudentKeys || []);
        const lockedFromCurrentMulti = dedupeStudentOptions(
            (Array.isArray(this.state.studentIds) ? this.state.studentIds : []).filter((item) =>
                lockedKeys.has(getStudentOptionKey(item))
            )
        );
        const lockedFromCurrentSingle =
            this.state.studentId && lockedKeys.has(getStudentOptionKey(this.state.studentId))
                ? [this.state.studentId]
                : [];
        const unlockedSelected = normalizedSelected.filter(
            (item) => !lockedKeys.has(getStudentOptionKey(item))
        );

        return dedupeStudentOptions([
            ...lockedFromCurrentMulti,
            ...lockedFromCurrentSingle,
            ...unlockedSelected,
        ]);
    };

    isLockedPrefilledEmail = (email) => {
        const normalizedEmail = String(email || '')
            .trim()
            .toLowerCase();
        if (!normalizedEmail) {
            return false;
        }
        return (this.state.lockedPrefilledEmails || []).includes(normalizedEmail);
    };

    isLockedPrefilledReferenceFile = (fileValue) => {
        const key = getReferenceFileCompareKey(getReferenceFileSource(fileValue));
        if (!key) {
            return false;
        }
        return (this.state.lockedPrefilledReferenceFileKeys || []).includes(key);
    };

    formatRescheduleDateTime = (sessionDate, timeValue) => {
        const directDateTime = moment(timeValue, 'YYYY-MM-DD HH:mm:ss', true);
        if (directDateTime.isValid()) {
            return directDateTime.format('YYYY-MM-DD HH:mm:ss');
        }

        const directMoment = moment(timeValue);
        if (directMoment.isValid()) {
            return directMoment.format('YYYY-MM-DD HH:mm:ss');
        }

        return moment(
            `${moment(sessionDate).format('YYYY-MM-DD')} ${timeValue}`,
            'YYYY-MM-DD HH:mm'
        ).format('YYYY-MM-DD HH:mm:ss');
    };

    fetchRescheduleSessionDetails = (visitId) => {
        this.props.getVisitInformation(visitId, (response) => {
            if (
                !response ||
                response.code !== RESPONSE_CONSTANT.SUCCESS_CODE ||
                !response.response
            ) {
                return;
            }

            const visitInfo = response.response;
            const tutee = visitInfo.tutee || {};
            const sessionUsers = Array.isArray(visitInfo.sessionUsers)
                ? visitInfo.sessionUsers
                : [];
            const externalUsersDetails = Array.isArray(visitInfo.externalUsersDetails)
                ? visitInfo.externalUsersDetails
                : [];
            const visitInfoExternalUserEmails = Array.isArray(visitInfo.externalUserEmails)
                ? visitInfo.externalUserEmails
                      .map((email) =>
                          String(email || '')
                              .trim()
                              .toLowerCase()
                      )
                      .filter(Boolean)
                : [];
            const cachedRescheduleExternalEmails = Array.isArray(
                this.state.rescheduleSessionData?.externalUserEmails
            )
                ? this.state.rescheduleSessionData.externalUserEmails
                      .map((email) =>
                          String(email || '')
                              .trim()
                              .toLowerCase()
                      )
                      .filter(Boolean)
                : Array.isArray(this.state.rescheduleSessionData?.externalEmails)
                  ? this.state.rescheduleSessionData.externalEmails
                        .map((email) =>
                            String(email || '')
                                .trim()
                                .toLowerCase()
                        )
                        .filter(Boolean)
                  : [];
            const visitExternalEmails = sessionUsers
                .filter((user) => user?.thirdParty === true && user?.email)
                .map((user) => String(user.email).toLowerCase());
            const visitExternalEmailsFromDetails =
                getExternalUserEmailsFromDetails(externalUsersDetails);
            const tuteeId = String(tutee.id || tutee.userId || '').trim();
            const tuteeSessionUser = tuteeId
                ? sessionUsers.find((user) => getSessionUserId(user) === tuteeId)
                : null;
            const patientSessionUser =
                sessionUsers.find(
                    (user) =>
                        !isSessionUserThirdParty(user) &&
                        String(user?.role || '')
                            .trim()
                            .toUpperCase() === 'PATIENT'
                ) || null;
            const firstSessionUser =
                sessionUsers.find((user) => user && (user.id || user.userId)) || null;
            const primaryStudent =
                tuteeSessionUser ||
                patientSessionUser ||
                firstSessionUser ||
                (tutee.id || tutee.userId ? tutee : null);
            const primarySessionUserRole = String(primaryStudent?.role || '')
                .trim()
                .toUpperCase();
            const isPrimaryRoleDoctor = primarySessionUserRole === 'DOCTOR';
            const isPrimaryRolePatient = primarySessionUserRole === 'PATIENT';
            const primaryStudentIsOwner = isSessionUserOwner(primaryStudent || {});
            const hasTuteeIdentity = !!String(tutee.id || tutee.userId || '').trim();
            const hasSingleParticipantIdentity = !!String(
                primaryStudent?.id ||
                    primaryStudent?.userId ||
                    this.state.rescheduleSessionData?.studentId ||
                    ''
            ).trim();

            const normalizedVisitSessionType = normalizeSessionType(
                visitInfo.sessionType || this.state.rescheduleSessionData?.sessionType || ''
            );

            const rawVisitType =
                visitInfo.visitType || this.state.rescheduleSessionData?.visitType || 0;
            let inferredVisitType = normalizeVisitTypeValue(rawVisitType);

            if (!inferredVisitType) {
                if (isInPersonSessionType(normalizedVisitSessionType)) {
                    inferredVisitType = 6;
                } else if (
                    isGroupSessionType(normalizedVisitSessionType) ||
                    sessionUsers.length > 1
                ) {
                    inferredVisitType = 4;
                } else if (isConferenceSessionType(normalizedVisitSessionType)) {
                    inferredVisitType = 8;
                } else if (visitExternalEmails.length > 0) {
                    // Fallback only when sessionType does not clearly identify group/in-person.
                    inferredVisitType = 8;
                } else {
                    inferredVisitType = 2;
                }
            }

            const shouldAssumeOwnerToOwner =
                inferredVisitType === 2 &&
                !hasTuteeIdentity &&
                hasSingleParticipantIdentity &&
                !isPrimaryRolePatient;
            const isOneToOneVisit = inferredVisitType === 2;

            const mergedRescheduleData = {
                ...(this.state.rescheduleSessionData || {}),
                subjectId:
                    visitInfo.expertiseCategoryId ||
                    visitInfo.subjectId ||
                    this.state.rescheduleSessionData?.subjectId ||
                    null,
                subjectLabel:
                    visitInfo.subject || this.state.rescheduleSessionData?.subjectLabel || '',
                subCategoryId:
                    visitInfo.expertiseId ||
                    visitInfo.subCategoryId ||
                    this.state.rescheduleSessionData?.subCategoryId ||
                    null,
                subCategoryLabel:
                    visitInfo.subCategory ||
                    this.state.rescheduleSessionData?.subCategoryLabel ||
                    '',
                studentId:
                    primaryStudent?.id ||
                    primaryStudent?.userId ||
                    this.state.rescheduleSessionData?.studentId ||
                    null,
                studentName:
                    primaryStudent?.name || this.state.rescheduleSessionData?.studentName || '',
                studentOwner:
                    isOneToOneVisit &&
                    (primaryStudentIsOwner ||
                        shouldAssumeOwnerToOwner ||
                        isTrueValue(this.state.rescheduleSessionData?.studentOwner)),
                ownerToOwner:
                    isOneToOneVisit &&
                    (isTrueValue(visitInfo.ownerToOwner) ||
                        primaryStudentIsOwner ||
                        isPrimaryRoleDoctor ||
                        shouldAssumeOwnerToOwner ||
                        (isPrimaryRolePatient
                            ? false
                            : isTrueValue(this.state.rescheduleSessionData?.ownerToOwner))),
                hasTutee: hasTuteeIdentity,
                primaryParticipantRole: primarySessionUserRole,
                sessionUsers: sessionUsers.map((user) => {
                    const normalizedRole = String(user?.role || '')
                        .trim()
                        .toUpperCase();
                    return {
                        id: getSessionUserId(user),
                        name: user?.name || '',
                        owner: normalizedRole === 'PATIENT' ? false : isSessionUserOwner(user),
                        thirdParty: isSessionUserThirdParty(user),
                        role: normalizedRole,
                    };
                }),
                externalUserEmails:
                    visitInfoExternalUserEmails.length > 0
                        ? visitInfoExternalUserEmails
                        : visitExternalEmailsFromDetails.length > 0
                          ? visitExternalEmailsFromDetails
                          : visitExternalEmails.length > 0
                            ? visitExternalEmails
                            : cachedRescheduleExternalEmails,
                externalUsersDetails: Array.isArray(visitInfo.externalUsersDetails)
                    ? visitInfo.externalUsersDetails
                    : Array.isArray(this.state.rescheduleSessionData?.externalUsersDetails)
                      ? this.state.rescheduleSessionData.externalUsersDetails
                      : [],
                comments: visitInfo.comments || this.state.rescheduleSessionData?.comments || '',
                topic:
                    visitInfo.topic ||
                    visitInfo.comments ||
                    this.state.rescheduleSessionData?.topic ||
                    '',
                sessionDate:
                    visitInfo.sessionDate || this.state.rescheduleSessionData?.sessionDate || '',
                sessionStartTime:
                    visitInfo.sessionStartDate ||
                    visitInfo.sessionStartTime ||
                    this.state.rescheduleSessionData?.sessionStartTime ||
                    '',
                sessionEndTime:
                    visitInfo.sessionEndDate ||
                    visitInfo.sessionEndTime ||
                    this.state.rescheduleSessionData?.sessionEndTime ||
                    '',
                sessionType:
                    visitInfo.sessionType || this.state.rescheduleSessionData?.sessionType || '',
                visitType: inferredVisitType,
                transcriptionLanguage:
                    visitInfo.transcriptionLanguage ||
                    this.state.rescheduleSessionData?.transcriptionLanguage ||
                    '',
                referenceFiles:
                    visitInfo.referenceFiles ||
                    visitInfo.soapNote?.visitComplaintImages ||
                    visitInfo.soapNote?.materialImages ||
                    this.state.rescheduleSessionData?.referenceFiles ||
                    [],
                inPersonEmail:
                    visitInfo.inPersonEmail ||
                    visitInfo.channelEmail ||
                    this.state.rescheduleSessionData?.inPersonEmail ||
                    this.state.rescheduleSessionData?.channelEmail ||
                    this.state.rescheduleSessionData?.selectedChannelEmail ||
                    '',
                hasStartedSession: isTrueValue(
                    visitInfo.hasStartedSession ||
                        this.state.rescheduleSessionData?.hasStartedSession
                ),
            };

            this.setState(
                {
                    rescheduleSessionData: mergedRescheduleData,
                    hasAppliedReschedulePrefill: false,
                    isReschedulePrefillPending: true,
                },
                () => {
                    this.applyReschedulePrefill(this.state.subjectList || []);
                }
            );
        });
    };

    applyReschedulePrefill = (subjectList = []) => {
        const { rescheduleSessionData, hasAppliedReschedulePrefill } = this.state;

        const shouldRetrySubjectMapping =
            hasAppliedReschedulePrefill &&
            !this.state.subjectId &&
            Array.isArray(subjectList) &&
            subjectList.length > 0;

        if (!rescheduleSessionData || (hasAppliedReschedulePrefill && !shouldRetrySubjectMapping)) {
            return;
        }

        const startMoment = moment(
            rescheduleSessionData.sessionStartTime,
            ['YYYY-MM-DD HH:mm:ss', 'HH:mm'],
            true
        );
        const endMoment = moment(
            rescheduleSessionData.sessionEndTime,
            ['YYYY-MM-DD HH:mm:ss', 'HH:mm'],
            true
        );
        const dateMoment = moment(
            rescheduleSessionData.sessionDate || rescheduleSessionData.sessionStartTime,
            ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss'],
            true
        );

        const subjectOption = (subjectList || []).find(
            (subject) =>
                String(subject.value) === String(rescheduleSessionData.subjectId) ||
                subject.label === rescheduleSessionData.subjectLabel
        );

        const normalizedSessionType = normalizeSessionType(rescheduleSessionData.sessionType || '');
        const numericVisitType = normalizeVisitTypeValue(rescheduleSessionData.visitType);
        const isConferenceSessionPrefill =
            numericVisitType === 8 || isConferenceSessionType(normalizedSessionType);
        const isInPersonSessionPrefill =
            numericVisitType === 6 || isInPersonSessionType(normalizedSessionType);
        const isGroupSession = numericVisitType === 4 || isGroupSessionType(normalizedSessionType);

        const prefillDate = dateMoment.isValid() ? dateMoment.toDate() : '';
        const prefillStartTime = startMoment.isValid()
            ? {
                  value: startMoment.format('YYYY-MM-DD HH:mm:ss'),
                  label: startMoment.format('hh:mm A'),
              }
            : '';
        const prefillEndTime = endMoment.isValid()
            ? {
                  value: endMoment.format('YYYY-MM-DD HH:mm:ss'),
                  label: endMoment.format('hh:mm A'),
              }
            : '';

        const primaryParticipantRole = String(
            rescheduleSessionData.primaryParticipantRole || ''
        ).toUpperCase();
        const prefillStudentId =
            rescheduleSessionData.studentId && rescheduleSessionData.studentName
                ? {
                      value: rescheduleSessionData.studentId,
                      id: rescheduleSessionData.studentId,
                      label: rescheduleSessionData.studentName,
                      owner:
                          primaryParticipantRole === 'PATIENT'
                              ? false
                              : isTrueValue(
                                    rescheduleSessionData.ownerToOwner ||
                                        rescheduleSessionData.studentOwner
                                ),
                  }
                : '';

        const prefillStudentIds = Array.isArray(rescheduleSessionData.sessionUsers)
            ? rescheduleSessionData.sessionUsers
                  .filter((user) => user && user.id && !user.thirdParty)
                  .map((user) => ({
                      id: user.id,
                      value: user.id,
                      label: user.name || '',
                      owner:
                          String(user?.role || '').toUpperCase() === 'PATIENT'
                              ? false
                              : isTrueValue(user?.owner),
                  }))
            : [];
        const uniquePrefillStudentIds = dedupeStudentOptions(prefillStudentIds);

        const prefillEmails = Array.isArray(rescheduleSessionData.externalUserEmails)
            ? rescheduleSessionData.externalUserEmails
                  .map((email) =>
                      String(email || '')
                          .trim()
                          .toLowerCase()
                  )
                  .filter(Boolean)
            : getExternalUserEmailsFromDetails(rescheduleSessionData.externalUsersDetails).filter(
                  Boolean
              );

        const prefillStudentOptions = (() => {
            if (uniquePrefillStudentIds.length) {
                return uniquePrefillStudentIds;
            }
            if (prefillStudentId && prefillStudentId.id) {
                return [
                    {
                        id: prefillStudentId.id,
                        value: prefillStudentId.id,
                        label: prefillStudentId.label || '',
                        owner: isTrueValue(prefillStudentId.owner),
                    },
                ];
            }
            return [];
        })();

        const prefillFiles = Array.isArray(rescheduleSessionData.referenceFiles)
            ? rescheduleSessionData.referenceFiles
                  .map((file) => getReferenceFileSource(file))
                  .filter(Boolean)
            : [];

        const prefillChannelEmail = String(
            subjectOption?.inPersonEmail ||
                rescheduleSessionData.inPersonEmail ||
                rescheduleSessionData.channelEmail ||
                rescheduleSessionData.selectedChannelEmail ||
                ''
        ).trim();
        const normalizedPrefillChannelEmail = String(prefillChannelEmail || '')
            .trim()
            .toLowerCase();
        const shouldPrefillInvitePatientToggle =
            !!normalizedPrefillChannelEmail &&
            prefillEmails.some((email) => email === normalizedPrefillChannelEmail);
        const conferencePrefillInputEmails = prefillEmails.filter(
            (email) => email !== normalizedPrefillChannelEmail
        );

        const hasStartedSession = isTrueValue(rescheduleSessionData?.hasStartedSession);
        const lockedPrefilledStudentKeys = hasStartedSession
            ? dedupeStudentOptions(prefillStudentOptions)
                  .map((student) => getStudentOptionKey(student))
                  .filter(Boolean)
            : [];
        const lockedPrefilledEmails = hasStartedSession
            ? Array.from(
                  new Set(
                      prefillEmails
                          .map((email) =>
                              String(email || '')
                                  .trim()
                                  .toLowerCase()
                          )
                          .filter(Boolean)
                  )
              )
            : [];
        const lockedPrefilledReferenceFileKeys = hasStartedSession
            ? Array.from(
                  new Set(
                      prefillFiles
                          .map((file) => getReferenceFileCompareKey(getReferenceFileSource(file)))
                          .filter(Boolean)
                  )
              )
            : [];

        this.setState(
            {
                hasAppliedReschedulePrefill: true,
                subjectId: subjectOption || '',
                sessionLanguage:
                    LANGUAGE_OPTIONS.find(
                        (option) =>
                            option.value ===
                            String(rescheduleSessionData.transcriptionLanguage || '')
                                .trim()
                                .toLowerCase()
                    ) || DEFAULT_SESSION_LANGUAGE,
                selectedChannelEmail: prefillChannelEmail,
                date: prefillDate,
                startDate: prefillDate || new Date(),
                startTime: prefillStartTime,
                endTime: prefillEndTime,
                summary: rescheduleSessionData.comments || rescheduleSessionData.topic || '',
                comments: rescheduleSessionData.comments || rescheduleSessionData.topic || '',
                uploadedImage: prefillFiles,
                isOneToOne:
                    !isGroupSession && !isConferenceSessionPrefill && !isInPersonSessionPrefill,
                conference: isConferenceSessionPrefill,
                isInPerson: isInPersonSessionPrefill,
                role: isTrueValue(
                    rescheduleSessionData.ownerToOwner || rescheduleSessionData.studentOwner
                ),
                studentId: !isGroupSession && !isConferenceSessionPrefill ? prefillStudentId : '',
                studentIds:
                    isGroupSession || isConferenceSessionPrefill ? uniquePrefillStudentIds : [],
                studentList: prefillStudentOptions,
                invitePatient: shouldPrefillInvitePatientToggle,
                emails: isConferenceSessionPrefill ? conferencePrefillInputEmails : [],
                currentEmail: '',
                isReschedulePrefillPending: false,
                hasStartedSession,
                lockedPrefilledStudentKeys,
                lockedPrefilledEmails,
                lockedPrefilledReferenceFileKeys,
                conferenceParticipantRemovalTouched: false,
            },
            () => {
                if (subjectOption?.value) {
                    this.getEducatorSubCategoryList(subjectOption.value, (subCategoryList) => {
                        const subCategoryOption = (subCategoryList || []).find(
                            (subCategory) =>
                                String(subCategory.value) ===
                                    String(rescheduleSessionData.subCategoryId) ||
                                subCategory.label === rescheduleSessionData.subCategoryLabel
                        );

                        if (subCategoryOption) {
                            this.setState({ subCategoryId: subCategoryOption });
                        }
                    });
                }

                if (prefillDate) {
                    this.getAvailableSlots(
                        prefillDate,
                        (slots = []) => {
                            const prefillStartClock = prefillStartTime
                                ? moment(prefillStartTime.value).format('HH:mm')
                                : '';
                            const prefillEndClock = prefillEndTime
                                ? moment(prefillEndTime.value).format('HH:mm')
                                : '';

                            const matchedSlot = (slots || []).find((slot) => {
                                const slotStart = moment(slot.startTime).format('HH:mm');
                                const slotEnd = moment(slot.endTime).format('HH:mm');
                                return (
                                    slotStart === prefillStartClock && slotEnd === prefillEndClock
                                );
                            });

                            if (matchedSlot?.id) {
                                store.set('slotIds', [matchedSlot.id]);
                                this.setState(
                                    {
                                        timeApp: [{ slotId: matchedSlot.id }],
                                        myValues: [matchedSlot.id],
                                    },
                                    () => {
                                        this.getAvailableStudentList();
                                    }
                                );
                            } else {
                                // No exact slot match found, but still fetch available students
                                // based on the prefilled start/end times
                                this.setState({ isReschedulePrefillPending: false }, () => {
                                    this.getAvailableStudentList();
                                });
                            }
                        },
                        { isEdit: true }
                    );
                } else {
                    this.setState({ isReschedulePrefillPending: false });
                }
            }
        );
    };

    submitRescheduleUpdate = (updatedValues) => {
        const {
            rescheduleVisitId,
            date,
            startTime,
            endTime,
            rescheduleSessionData,
            studentIds,
            emails,
            currentEmail,
            invitePatient,
            selectedChannelEmail,
        } = this.state;
        const hasStartedSession = isTrueValue(rescheduleSessionData?.hasStartedSession);
        const rescheduleStudentId = String(rescheduleSessionData?.studentId || '').trim();
        const rescheduleRoleFromStudentId = String(
            (Array.isArray(rescheduleSessionData?.sessionUsers)
                ? rescheduleSessionData.sessionUsers
                : []
            ).find((user) => getSessionUserId(user) === rescheduleStudentId)?.role || ''
        )
            .trim()
            .toUpperCase();
        const reschedulePrimaryRole = String(
            rescheduleSessionData?.primaryParticipantRole ||
                rescheduleRoleFromStudentId ||
                rescheduleSessionData?.sessionUsers?.find((user) => !user?.thirdParty)?.role ||
                ''
        )
            .trim()
            .toUpperCase();
        const usePatientSideFlow = reschedulePrimaryRole === 'PATIENT' && this.state.isOneToOne;

        const toIdArray = (value) =>
            Array.from(
                new Set(
                    (Array.isArray(value) ? value : [])
                        .map((item) => {
                            if (typeof item === 'object' && item !== null) {
                                return String(item.id || item.value || '').trim();
                            }
                            return String(item || '').trim();
                        })
                        .filter(Boolean)
                )
            );

        const toEmailArray = (value) =>
            Array.from(
                new Set(
                    (Array.isArray(value) ? value : [])
                        .map((email) => {
                            if (typeof email === 'object' && email !== null) {
                                return String(email.value || email.email || '')
                                    .trim()
                                    .toLowerCase();
                            }
                            return String(email || '')
                                .trim()
                                .toLowerCase();
                        })
                        .filter(Boolean)
                )
            );

        const toReferenceFiles = (value) =>
            (Array.isArray(value) ? value : [])
                .map((file) => {
                    return getReferenceFileSource(file);
                })
                .filter(Boolean);

        const toReferenceFileEntries = (value) =>
            (Array.isArray(value) ? value : [])
                .map((file) => ({
                    id: getReferenceFileId(file),
                    source: getReferenceFileSource(file),
                    compareKey: getReferenceFileCompareKey(getReferenceFileSource(file)),
                }))
                .filter((entry) => !!entry.source);

        const getDiff = (originalList = [], currentList = []) => {
            const originalSet = new Set(originalList);
            const currentSet = new Set(currentList);
            const added = currentList.filter((item) => !originalSet.has(item));
            const removed = originalList.filter((item) => !currentSet.has(item));
            return { added, removed };
        };

        const selectedDate = moment(date).format('YYYY-MM-DD');
        const originalSessionDate =
            rescheduleSessionData?.sessionDate ||
            (rescheduleSessionData?.sessionStartTime
                ? moment(rescheduleSessionData.sessionStartTime).format('YYYY-MM-DD')
                : selectedDate);

        const originalSessionUsers = Array.isArray(rescheduleSessionData?.sessionUsers)
            ? rescheduleSessionData.sessionUsers
            : [];

        // Updated selection owner hints must not influence original participant classification.
        const newSelectionOwnerIds = new Set([
            ...toIdArray(
                (Array.isArray(updatedValues?.studentIds) ? updatedValues.studentIds : [])
                    .filter((user) => typeof user === 'object' && isTrueValue(user?.owner))
                    .map((user) => user?.id || user?.value)
            ),
            ...toIdArray(Array.isArray(updatedValues?.ownerIds) ? updatedValues.ownerIds : []),
        ]);

        const knownOwnerIds = new Set([
            ...toIdArray(
                (Array.isArray(this.state.studentList) ? this.state.studentList : [])
                    .filter((user) => isTrueValue(user?.owner))
                    .map((user) => user?.id || user?.value)
            ),
            ...toIdArray(
                (Array.isArray(studentIds) ? studentIds : [])
                    .filter((user) => isTrueValue(user?.owner))
                    .map((user) => user?.id || user?.value)
            ),
        ]);

        const isKnownOwner = (user) => {
            // Classify original participants strictly from visit/session user role metadata.
            // Available-student owner flags can differ and should only influence new selections.
            return !!getSessionUserId(user) && isSessionUserOwner(user);
        };

        let originalStudentIds = toIdArray(
            originalSessionUsers
                .filter(
                    (user) =>
                        getSessionUserId(user) &&
                        !isKnownOwner(user) &&
                        !isSessionUserThirdParty(user)
                )
                .map((user) => getSessionUserId(user))
        );
        let originalOwnerIds = toIdArray(
            originalSessionUsers
                .filter(
                    (user) =>
                        getSessionUserId(user) &&
                        isKnownOwner(user) &&
                        !isSessionUserThirdParty(user)
                )
                .map((user) => getSessionUserId(user))
        );

        const originalSingleParticipantId = String(rescheduleSessionData?.studentId || '').trim();
        const originalSingleParticipantIsOwner = isTrueValue(
            rescheduleSessionData?.ownerToOwner || rescheduleSessionData?.studentOwner
        );

        if (!originalStudentIds.length && !originalOwnerIds.length && originalSingleParticipantId) {
            if (originalSingleParticipantIsOwner) {
                originalOwnerIds = [originalSingleParticipantId];
            } else {
                originalStudentIds = [originalSingleParticipantId];
            }
        }

        // For one-to-one owner sessions, force the prefilled participant id into owner bucket.
        // This prevents owner ids from being misclassified as students in remove diff payloads.
        if (originalSingleParticipantIsOwner && originalSingleParticipantId) {
            originalOwnerIds = toIdArray([...originalOwnerIds, originalSingleParticipantId]);
            originalStudentIds = originalStudentIds.filter(
                (id) => id !== originalSingleParticipantId
            );
        }

        if (usePatientSideFlow && this.state.isOneToOne) {
            const patientBaselineStudentId =
                originalSingleParticipantId || originalStudentIds[0] || '';
            originalStudentIds = patientBaselineStudentId
                ? toIdArray([patientBaselineStudentId])
                : toIdArray(originalStudentIds);
            originalOwnerIds = [];
        }

        const knownStudentIds = new Set([
            ...originalStudentIds,
            ...toIdArray(
                (Array.isArray(this.state.studentList) ? this.state.studentList : [])
                    .filter((user) => !isTrueValue(user?.owner))
                    .map((user) => user?.id || user?.value)
            ),
        ]);

        const normalizeParticipantBuckets = (studentIdList = [], ownerIdList = []) => {
            if (usePatientSideFlow && this.state.isOneToOne) {
                return {
                    students: toIdArray(studentIdList),
                    owners: [],
                };
            }

            const students = new Set(toIdArray(studentIdList));
            const owners = new Set(toIdArray(ownerIdList));
            const originalOwnerIdSet = new Set(originalOwnerIds);
            const originalStudentIdSet = new Set(originalStudentIds);
            const effectiveOwnerIds = new Set([...knownOwnerIds, ...newSelectionOwnerIds]);

            Array.from(students).forEach((id) => {
                if (effectiveOwnerIds.has(id) && !knownStudentIds.has(id)) {
                    students.delete(id);
                    owners.add(id);
                }
            });

            Array.from(owners).forEach((id) => {
                if (knownStudentIds.has(id) && !effectiveOwnerIds.has(id)) {
                    owners.delete(id);
                    students.add(id);
                }
            });

            Array.from(students).forEach((id) => {
                if (owners.has(id)) {
                    students.delete(id);
                }
            });

            // Keep original participant role stable unless explicitly moved.
            Array.from(students).forEach((id) => {
                if (originalOwnerIdSet.has(id) && !originalStudentIdSet.has(id)) {
                    students.delete(id);
                    owners.add(id);
                }
            });

            Array.from(owners).forEach((id) => {
                if (originalStudentIdSet.has(id) && !originalOwnerIdSet.has(id)) {
                    owners.delete(id);
                    students.add(id);
                }
            });

            return {
                students: Array.from(students),
                owners: Array.from(owners),
            };
        };

        const externalEmailsFromRescheduleState = toEmailArray(
            rescheduleSessionData?.externalUserEmails
        );
        const externalEmailsFromDetails = toEmailArray(
            getExternalUserEmailsFromDetails(rescheduleSessionData?.externalUsersDetails)
        );
        const externalEmailsFromSessionUsers = toEmailArray(
            originalSessionUsers
                .filter((user) => user?.thirdParty && user?.email)
                .map((user) => user.email)
        );
        const externalEmailsFromChannelSelection = toEmailArray(
            rescheduleSessionData?.selectedChannelEmail ||
                rescheduleSessionData?.channelEmail ||
                rescheduleSessionData?.inPersonEmail
        );

        const originalExternalEmails =
            externalEmailsFromRescheduleState.length > 0
                ? externalEmailsFromRescheduleState
                : externalEmailsFromDetails.length > 0
                  ? externalEmailsFromDetails
                  : externalEmailsFromSessionUsers.length > 0
                    ? externalEmailsFromSessionUsers
                    : externalEmailsFromChannelSelection;

        const stateStudentIds = (Array.isArray(studentIds) ? studentIds : []).filter(
            (user) => !isTrueValue(user?.owner)
        );
        const stateOwnerIds = (Array.isArray(studentIds) ? studentIds : []).filter(
            (user) => isTrueValue(user?.owner)
        );

        const pendingEmail = String(currentEmail || '')
            .trim()
            .toLowerCase();

        const externalUserEmailsFromState = [
            ...toEmailArray(emails),
            ...(pendingEmail && validateEmail(pendingEmail) ? [pendingEmail] : []),
        ];

        const updatedStudentIds = toIdArray(updatedValues?.studentIds);
        const updatedOwnerIds = toIdArray(updatedValues?.ownerIds);
        const updatedExternalEmails = toEmailArray(updatedValues?.externalUserEmails);
        const normalizedUpdatedOwnerToOwner = isTrueValue(
            updatedValues?.selectedStudentIsOwner ?? updatedValues?.ownerToOwner
        );
        const effectiveOwnerToOwner = usePatientSideFlow
            ? false
            : normalizedUpdatedOwnerToOwner;

        const normalizedUpdatedBuckets = normalizeParticipantBuckets(
            updatedStudentIds,
            updatedOwnerIds
        );
        const hasExplicitParticipantArrays =
            Array.isArray(updatedValues?.studentIds) || Array.isArray(updatedValues?.ownerIds);

        const currentStudentIds = hasExplicitParticipantArrays
            ? normalizedUpdatedBuckets.students
            : normalizedUpdatedBuckets.students.length > 0 ||
                normalizedUpdatedBuckets.owners.length > 0
              ? normalizedUpdatedBuckets.students
              : toIdArray(
                    !effectiveOwnerToOwner && updatedValues?.studentId
                        ? [updatedValues.studentId]
                        : stateStudentIds
                );
        const currentOwnerIds = hasExplicitParticipantArrays
            ? normalizedUpdatedBuckets.owners
            : normalizedUpdatedBuckets.students.length > 0 ||
                normalizedUpdatedBuckets.owners.length > 0
              ? normalizedUpdatedBuckets.owners
              : toIdArray(
                    effectiveOwnerToOwner && updatedValues?.studentId
                        ? [updatedValues.studentId]
                        : stateOwnerIds
                );
        const currentExternalEmails =
            updatedExternalEmails.length > 0
                ? updatedExternalEmails
                : toEmailArray(externalUserEmailsFromState);
        const originalReferenceFileEntries = toReferenceFileEntries(
            rescheduleSessionData?.referenceFiles
        );
        const originalReferenceFileKeys = originalReferenceFileEntries
            .map((entry) => entry.compareKey)
            .filter(Boolean);
        const currentReferenceFiles = toReferenceFiles(
            updatedValues?.referenceFiles || this.state.uploadedImage
        );
        const currentReferenceFileEntries = toReferenceFileEntries(
            updatedValues?.referenceFiles || this.state.uploadedImage
        );
        const currentReferenceFileKeys = currentReferenceFileEntries
            .map((entry) => entry.compareKey)
            .filter(Boolean);

        const studentDiff = getDiff(originalStudentIds, currentStudentIds);
        const ownerDiff = getDiff(originalOwnerIds, currentOwnerIds);
        const normalizedRemovedBuckets = (() => {
            const removedIds = toIdArray([...studentDiff.removed, ...ownerDiff.removed]);
            const normalizedRemovedStudentIds = [];
            const normalizedRemovedOwnerIds = [];

            removedIds.forEach((id) => {
                const normalizedId = String(id || '').trim();
                const isOriginalStudent = originalStudentIds.includes(normalizedId);
                const isOriginalOwner = originalOwnerIds.includes(normalizedId);

                if (isOriginalOwner && !isOriginalStudent) {
                    normalizedRemovedOwnerIds.push(normalizedId);
                    return;
                }

                if (isOriginalStudent && !isOriginalOwner) {
                    normalizedRemovedStudentIds.push(normalizedId);
                    return;
                }

                if (
                    ownerDiff.removed.includes(normalizedId) &&
                    !studentDiff.removed.includes(normalizedId)
                ) {
                    normalizedRemovedOwnerIds.push(normalizedId);
                    return;
                }

                normalizedRemovedStudentIds.push(normalizedId);
            });

            return {
                students: toIdArray(normalizedRemovedStudentIds),
                owners: toIdArray(normalizedRemovedOwnerIds),
            };
        })();
        const referenceFileDiff = getDiff(originalReferenceFileKeys, currentReferenceFileKeys);
        const addedReferenceFiles = currentReferenceFileEntries
            .filter((entry) => referenceFileDiff.added.includes(entry.compareKey))
            .map((entry) => entry.source);
        const removedReferenceFileIds = Array.from(
            new Set(
                originalReferenceFileEntries
                    .filter(
                        (entry) =>
                            !!entry.id && referenceFileDiff.removed.includes(entry.compareKey)
                    )
                    .map((entry) => entry.id)
            )
        );
        const visitType = normalizeVisitTypeValue(
            updatedValues?.visitType ||
                rescheduleSessionData?.visitType ||
                (this.state.isOneToOne ? 2 : 0)
        );
        const normalizedRescheduleSessionType = normalizeSessionType(
            updatedValues?.sessionType || rescheduleSessionData?.sessionType || ''
        );
        const isConferenceReschedule =
            visitType === 8 ||
            isConferenceSessionType(normalizedRescheduleSessionType) ||
            this.state.conference === true;
        const conferenceExternalEmailsFromState = toEmailArray([
            ...toEmailArray(emails),
            ...(pendingEmail && validateEmail(pendingEmail) ? [pendingEmail] : []),
            ...(invitePatient && selectedChannelEmail
                ? Array.isArray(selectedChannelEmail)
                    ? selectedChannelEmail
                    : [selectedChannelEmail]
                : []),
        ]);
        const effectiveCurrentExternalEmails = isConferenceReschedule
            ? conferenceExternalEmailsFromState
            : currentExternalEmails;
        const externalDiff = getDiff(originalExternalEmails, effectiveCurrentExternalEmails);
        const isExplicitParticipantArrayUpdate =
            Array.isArray(updatedValues?.studentIds) || Array.isArray(updatedValues?.ownerIds);
        const hasOneToOneVisitType = visitType === 2;
        const hasOneToOneSessionType =
            !isConferenceSessionType(normalizedRescheduleSessionType) &&
            !isGroupSessionType(normalizedRescheduleSessionType) &&
            !isInPersonSessionType(normalizedRescheduleSessionType);
        const isOneToOneReschedule =
            !isExplicitParticipantArrayUpdate &&
            (hasOneToOneVisitType ||
                this.state.isOneToOne ||
                hasOneToOneSessionType ||
                normalizedUpdatedOwnerToOwner ||
                isTrueValue(rescheduleSessionData?.ownerToOwner) ||
                !!updatedValues?.studentId);

        const originalSingleStudentId =
            originalStudentIds[0] || String(rescheduleSessionData?.studentId || '').trim();
        const originalSingleOwnerId = originalOwnerIds[0] || '';

        const payload = {
            visitId: rescheduleVisitId,
            sessionDate: originalSessionDate,
            newSessionDate: selectedDate,
            sessionStartTime: this.formatRescheduleDateTime(date, startTime.value),
            sessionEndTime: this.formatRescheduleDateTime(date, endTime.value),
            expertiseCategoryId: updatedValues?.expertiseCategoryId || null,
            expertiseId: updatedValues?.expertiseId || null,
            topic: updatedValues?.topic || '',
            ...(AppConfig?.isHealthCare === true
                ? {
                      transcriptionLanguage:
                          updatedValues?.transcriptionLanguage ||
                          this.state.sessionLanguage?.value ||
                          DEFAULT_TRANSCRIPTION_LANGUAGE,
                  }
                : {}),
            referenceFiles: currentReferenceFiles,
        };

        // For one-to-one updates, decide ownerId/studentId from the current selected participant.
        // Using direct selection avoids diff/bucket edge cases (e.g., Student -> Owner replacement).
        if (!hasStartedSession && isOneToOneReschedule) {
            const selectedParticipantId = String(updatedValues?.studentId || '').trim();
            const hasExplicitSelectedOwnerFlag =
                typeof updatedValues?.selectedStudentIsOwner !== 'undefined';
            const explicitSelectedIsOwner = isTrueValue(updatedValues?.selectedStudentIsOwner);
            const isSelectedParticipantOwner = hasExplicitSelectedOwnerFlag
                ? explicitSelectedIsOwner
                : normalizedUpdatedOwnerToOwner ||
                  knownOwnerIds.has(selectedParticipantId) ||
                  newSelectionOwnerIds.has(selectedParticipantId) ||
                  originalOwnerIds.includes(selectedParticipantId);

            if (!selectedParticipantId) {
                delete payload.ownerId;
                delete payload.studentId;
            } else if (isSelectedParticipantOwner) {
                const hasOwnerChanged =
                    selectedParticipantId !== originalSingleOwnerId ||
                    originalStudentIds.length > 0 ||
                    ownerDiff.added.includes(selectedParticipantId);

                if (hasOwnerChanged) {
                    payload.ownerId = selectedParticipantId;
                    delete payload.studentId;
                }
            } else {
                const hasStudentChanged =
                    selectedParticipantId !== originalSingleStudentId ||
                    originalOwnerIds.length > 0 ||
                    studentDiff.added.includes(selectedParticipantId);

                if (hasStudentChanged) {
                    payload.studentId = selectedParticipantId;
                    delete payload.ownerId;
                }
            }
        }

        if (!isOneToOneReschedule) {
            if (studentDiff.added.length) {
                payload.addStudentIds = studentDiff.added;
            }
            const allowConferenceRemovalPayload =
                !isConferenceReschedule || this.state.conferenceParticipantRemovalTouched;
            if (allowConferenceRemovalPayload && !hasStartedSession) {
                const removedStudentIds = usePatientSideFlow
                    ? toIdArray([...studentDiff.removed, ...ownerDiff.removed])
                    : normalizedRemovedBuckets.students;
                if (removedStudentIds.length) {
                    payload.removeStudentIds = removedStudentIds;
                }
            }
            if (ownerDiff.added.length) {
                payload.addOwnerIds = ownerDiff.added;
            }
            if (
                !usePatientSideFlow &&
                allowConferenceRemovalPayload &&
                !hasStartedSession &&
                normalizedRemovedBuckets.owners.length
            ) {
                payload.removeOwnerIds = normalizedRemovedBuckets.owners;
            }
            if (usePatientSideFlow) {
                delete payload.removeOwnerIds;
            }
        }

        if (externalDiff.added.length) {
            payload.addExternalEmails = externalDiff.added;
        }
        if ((isConferenceReschedule || !hasStartedSession) && externalDiff.removed.length) {
            payload.removeExternalEmails = externalDiff.removed;
        }

        if (isOneToOneReschedule) {
            delete payload.addStudentIds;
            delete payload.removeStudentIds;
            delete payload.addOwnerIds;
            delete payload.removeOwnerIds;
        }

        // Conference participant updates can contain owner-only replacements.
        // In that case, do not send removeStudentIds when no student is selected now.
        if (
            isConferenceReschedule &&
            !usePatientSideFlow &&
            !currentStudentIds.length &&
            Array.isArray(payload.removeStudentIds) &&
            payload.removeStudentIds.length
        ) {
            const knownOwnerRemovalIds = payload.removeStudentIds.filter(
                (id) =>
                    originalOwnerIds.includes(String(id || '').trim()) &&
                    !originalStudentIds.includes(String(id || '').trim())
            );
            const knownStudentRemovalIds = payload.removeStudentIds.filter(
                (id) =>
                    !originalOwnerIds.includes(String(id || '').trim()) ||
                    originalStudentIds.includes(String(id || '').trim())
            );

            if (knownOwnerRemovalIds.length) {
                payload.removeOwnerIds = toIdArray([
                    ...(Array.isArray(payload.removeOwnerIds) ? payload.removeOwnerIds : []),
                    ...knownOwnerRemovalIds,
                ]);
            }

            if (knownStudentRemovalIds.length) {
                payload.removeStudentIds = knownStudentRemovalIds;
            } else {
                delete payload.removeStudentIds;
            }
        }

        // Keep removal payloads only for participants that are truly absent in current selection.
        const originalParticipantIds = toIdArray([...originalStudentIds, ...originalOwnerIds]);
        const currentParticipantIds = toIdArray([...currentStudentIds, ...currentOwnerIds]);
        const actuallyRemovedParticipantIds = new Set(
            originalParticipantIds.filter((id) => !currentParticipantIds.includes(id))
        );

        if (Array.isArray(payload.removeStudentIds)) {
            payload.removeStudentIds = payload.removeStudentIds.filter((id) =>
                actuallyRemovedParticipantIds.has(String(id || '').trim())
            );
            if (!payload.removeStudentIds.length) {
                delete payload.removeStudentIds;
            }
        }

        if (Array.isArray(payload.removeOwnerIds)) {
            payload.removeOwnerIds = payload.removeOwnerIds.filter((id) =>
                actuallyRemovedParticipantIds.has(String(id || '').trim())
            );
            if (!payload.removeOwnerIds.length) {
                delete payload.removeOwnerIds;
            }
        }

        if (addedReferenceFiles.length) {
            payload.addedReferenceFiles = addedReferenceFiles;
        }
        if (removedReferenceFileIds.length) {
            payload.removedReferenceFileIds = removedReferenceFileIds;
        }

        // Hard guard: for owner-only one-to-one reschedule updates, do not send removeStudentIds.
        if (
            this.state.isOneToOne &&
            !usePatientSideFlow &&
            !isOneToOneReschedule &&
            !payload.ownerId &&
            !payload.studentId &&
            Array.isArray(payload.removeStudentIds) &&
            payload.removeStudentIds.length
        ) {
            payload.removeOwnerIds = toIdArray([
                ...(Array.isArray(payload.removeOwnerIds) ? payload.removeOwnerIds : []),
                ...payload.removeStudentIds,
            ]);
            delete payload.removeStudentIds;
        }

        this.props.rescheduleSession(payload, (res) => {
            this.setState({ isLoading: false });
            if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                this.props.showNotification(res.message, 'success');
                this.props.history.push(
                    `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
                );
            } else {
                this.props.showNotification(res.message, 'error');
            }
        });
    };

    // Get Student Details
    getAvailableStudentList() {
        const educatorId = this.getTutorId();
        if (!educatorId) {
            this.setState({ isStudentLoading: false });
            return;
        }

        // Fetch student based on Date, slots, educatorId
        this.setState({ isStudentLoading: true });
        let params = {
            sessionDate: moment(this.state.startDate).format('YYYY-MM-DD'),
            slotIds: this.state.myValues.toString(),
            educatorId,
            search: this.state.studentSearchValue,
            pageNumber: 1,
            itemsPerPage: 99999,
            educatorInsId: this.state.levelId?.value || this.state.levelId || '',
            sessionStartTime: this.state.startTime?.value || '',
            sessionEndTime: this.state.endTime?.value || '',
        };
        this.props.getAvailableStudentList(params, (result) => {
            if (result) {
                let studentDetails = [];
                if (result.response.data && result.response.data.length) {
                    result.response.data.forEach((element) => {
                        let name = element.name;
                        let obj = {
                            label: name,
                            value: element.id,
                            owner: isTrueValue(element.owner),
                            id: element.id,
                            activeStatus: element.activeStatus,
                        };
                        studentDetails.push(obj);
                    });
                }
                const uniqueStudentDetails = dedupeStudentOptions(studentDetails);
                this.setState({
                    isStudentLoading: false,
                    studentList: uniqueStudentDetails,
                });
            }
        });
    }

    // Mobile navigation show hide
    onClickMobileNav = (type) => {
        this.setState({ mobileNav: type });
    };

    // Reset form state values
    resetStateValue = () => {
        if (this.state.isRescheduleMode && this.state.hasAppliedReschedulePrefill) {
            // Preserve existing prefilled values while switching session type in reschedule mode.
            return;
        }

        if (this.state.isFromStudent) {
            this.setState({
                subjectId: '',
                subCategoryId: '',
                date: '',
                timeApp: [],
                studentIds: [],
                summary: '',
                studentList: [],
                uploadedImage: [],
                startTime: '',
                endTime: '',
                selectedChannelEmail: '',
                invitePatient: false,
                emails: [],
                currentEmail: '',
                conferenceParticipantRemovalTouched: false,
            });
        } else {
            this.setState({
                levelId:
                    this.props.userDetails.associatedLevels &&
                    this.props.userDetails.associatedLevels.length === 1
                        ? this.props.userDetails.associatedLevels[0]
                        : '',
                subjectId: '',
                subCategoryId: '',
                date: '',
                timeApp: [],
                studentId: '',
                studentIds: [],
                summary: '',
                studentList: [],
                uploadedImage: [],
                startTime: '',
                endTime: '',
                selectedChannelEmail: '',
                invitePatient: false,
                emails: [],
                currentEmail: '',
                conferenceParticipantRemovalTouched: false,
            });
        }
    };

    // RadioButton Checkbox Change

    handleOneToOneCheckboxChange = (e) => {
        const { name } = e.target;
        if (this.state.isFromStudent) {
            if (name === 'inperson') {
                this.setState({ isInPerson: true, isVirtuowl: false });
            } else {
                this.setState({ isVirtuowl: !this.state.isVirtuowl, isInPerson: false });
            }
        } else {
            if (name === 'onetoone') {
                this.setState({ isOneToOne: true, conference: false, isInPerson: false });
            } else if (name === 'classroom') {
                this.setState({ isOneToOne: false, conference: false, isInPerson: false });
            } else if (name === 'conference') {
                this.setState({ isOneToOne: false, conference: true, isInPerson: false });
            } else if (name === 'inperson') {
                this.setState({ isInPerson: true, isOneToOne: false, conference: false });
            }
        }
        this.resetStateValue();
    };

    // handleOneToOneCheckboxChange = (e) => {
    //     if (this.state.isFromStudent) {
    //         this.setState({ isVirtuowl: !this.state.isVirtuowl });
    //     } else {
    //         this.setState({ isOneToOne: !this.state.isOneToOne });
    //     }
    //     this.resetStateValue();
    // };

    // handleFileUpload

    handleFileUpload = (event) => {
        const files = event.target.files;
        const MAX_TOTAL_SIZE = 52428800;
        const MAX_SINGLE_FILE_SIZE = 10485760;
        let { totalUploadedSize = 0, uploadedImage = [] } = this.state;
        let newUploadSize = 0;
        const validFiles = [];
        const readerPromises = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (
                (file.type === 'image/png' ||
                    file.type === 'image/jpeg' ||
                    file.type === 'image/jpg' ||
                    file.type === 'application/pdf') &&
                file.size <= MAX_SINGLE_FILE_SIZE
            ) {
                newUploadSize += file.size;
                if (totalUploadedSize + newUploadSize > MAX_TOTAL_SIZE) {
                    this.setState({ isImageUploaded: false });
                    this.props.showNotification(
                        RESPONSE_CONSTANT.MEDIA_TOTAL_SIZE_EXCEEDED,
                        RESPONSE_CONSTANT.WARNING
                    );
                    return;
                }
                validFiles.push({ file, size: file.size });
            } else if (file.size > MAX_SINGLE_FILE_SIZE) {
                this.setState({ isImageUploaded: false });
                this.props.showNotification(
                    RESPONSE_CONSTANT.MEDIA_FILE_SIZE,
                    RESPONSE_CONSTANT.WARNING
                );
                return;
            } else {
                this.setState({ isImageUploaded: false });
                this.props.showNotification(
                    RESPONSE_CONSTANT.UPLOAD_ONLY_IMAGE,
                    RESPONSE_CONSTANT.WARNING
                );
                return;
            }
        }
        validFiles.forEach(({ file }) => {
            const reader = new FileReader();
            readerPromises.push(
                new Promise((resolve) => {
                    reader.onloadend = () => resolve({ result: reader.result, size: file.size });
                    reader.readAsDataURL(file);
                })
            );
        });
        Promise.all(readerPromises).then((base64Results) => {
            const newTotalSize = totalUploadedSize + newUploadSize;
            this.setState({
                uploadedImage: [...uploadedImage, ...base64Results.map((res) => res.result)],
                totalUploadedSize: newTotalSize,
            });
        });
    };

    // removeObject
    removeObject = (id, type) => {
        if (type === 'slot') {
            if (!this.state.isFromStudent) {
                this.setState({ studentId: '', studentIds: [] });
            }
            const newArray = this.state.timeApp;
            newArray.splice(id, 1);
            this.setState({ timeApp: newArray }, () => {
                this.getAvailableStudentList();
            });
            const slotIds = newArray.map((option) => option.slotId);
            store.set('slotIds', slotIds);
            this.setState({
                myValues: slotIds,
            });
        } else {
            const newArray = [...this.state.uploadedImage];
            const removedFile = newArray[id];
            const removedFileSize =
                removedFile && typeof removedFile === 'object' && removedFile.size
                    ? removedFile.size
                    : 0;
            newArray.splice(id, 1);
            const newTotalSize = this.state.totalUploadedSize - removedFileSize; // Recalculate the total size
            this.setState({
                uploadedImage: newArray,
                totalUploadedSize: newTotalSize,
            });
        }
    };

    // Create session when click create button
    // handleSubmit = () => {
    //     // if (
    //     //   !this.state.levelId &&
    //     //   this.props.userDetails.createdRole !== RESPONSE_CONSTANT.BRANCHADMIN
    //     // ) {
    //     //   this.props.showNotification(
    //     //     RESPONSE_CONSTANT.LEVEL_MANDATORY,
    //     //     RESPONSE_CONSTANT.WARNING
    //     //   );
    //     //   return;
    //     // }

    //     if (!this.state.subjectId) {
    //         this.props.showNotification(
    //             RESPONSE_CONSTANT.SUBJECT_MANDATORY,
    //             RESPONSE_CONSTANT.WARNING
    //         );
    //         return;
    //     }
    //     if (!this.state.subCategoryId) {
    //         this.props.showNotification(
    //             RESPONSE_CONSTANT.SUB_SUBJECT_MANDATORY,
    //             RESPONSE_CONSTANT.WARNING
    //         );
    //         return;
    //     }
    //     if (!this.state.date || !this.state.timeApp.length) {
    //         this.props.showNotification(
    //             RESPONSE_CONSTANT.SELECT_DATE_TIME,
    //             RESPONSE_CONSTANT.WARNING
    //         );
    //         return;
    //     }
    //     if (
    //         !this.state.isInPerson &&
    //         ((this.state.isOneToOne && !this.state.studentId) ||
    //             (!this.state.isOneToOne && this.state.studentIds && !this.state.studentIds.length))
    //     ) {
    //         this.props.showNotification(
    //             RESPONSE_CONSTANT.STUDENT_MANDATORY,
    //             RESPONSE_CONSTANT.WARNING
    //         );
    //         return;
    //     }

    //     // Safety check for userDetails.tutor
    //     if (
    //         !this.props.userDetails ||
    //         !this.props.userDetails.tutor ||
    //         !this.props.userDetails.tutor.id
    //     ) {
    //         this.props.showNotification(
    //             'Tutor information is missing. Please refresh the page.',
    //             RESPONSE_CONSTANT.ERROR
    //         );
    //         return;
    //     }

    //     this.setState({ isLoading: true });
    //     if (this.state.isOneToOne || this.state.isFromStudent) {
    //         let data = {
    //             sessionDate: moment(this.state.date).format('YYYY-MM-DD'),
    //             expertiseId: this.state.subCategoryId.value,
    //             expertiseCategoryId: this.state.subjectId.value,
    //             comments: this.state.comments,
    //             visitType:
    //                 !this.state.isFromStudent && this.state.isOneToOne
    //                     ? 2
    //                     : this.state.isVirtuowl
    //                       ? 2
    //                       : !this.state.isVirtuowl
    //                         ? 6
    //                         : 4,
    //             educatorInsId: this.props.userDetails.associatedLevels[0].id,
    //             tutorId: this.props.userDetails.tutor.id,
    //             studentId: this.state.studentId?.value
    //                 ? this.state.studentId.value
    //                 : this.state.studentId.id,
    //             topic: this.state.summary,
    //             referenceFiles: this.state.uploadedImage,
    //             sessionStartTime: this.state.startTime.value,
    //             sessionEndTime: this.state.endTime.value,
    //             ownerToOwner: this.state.role,
    //             inPersonEmail: this.state.selectedChannelEmail,
    //         };
    //         this.props.appointmentBook(data, (res) => {
    //             this.setState({ isLoading: false });
    //             if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
    //                 this.props.showNotification(res.message, 'success');
    //                 this.props.history.push(
    //                     `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
    //                 );
    //             } else {
    //                 this.props.showNotification(res.message, 'error');
    //             }
    //         });
    //     } else {
    //         let studentIds = [];
    //         let ownerIds = [];
    //         if (this.state.studentIds && this.state.studentIds.length) {
    //             this.state.studentIds.forEach((element) => {
    //                 if (element.owner) {
    //                     ownerIds.push(element.id);
    //                 } else {
    //                     studentIds.push(element.id);
    //                 }
    //             });
    //         }
    //         let classRoomDataParams = {
    //             sessionDate: moment(this.state.date).format('YYYY-MM-DD'),
    //             expertiseId: this.state.subCategoryId.value,
    //             expertiseCategoryId: this.state.subjectId.value,
    //             slotIds: store.get('slotIds'),
    //             comments: this.state.comments,
    //             visitType: this.state.isOneToOne ? 2 : 4,
    //             educatorInsId: this.state.levelId.value,
    //             educatorId: this.props.userDetails.tutor.id,
    //             studentIds: studentIds,
    //             ownerIds: ownerIds,
    //             topic: this.state.summary,
    //             referenceFiles: this.state.uploadedImage,
    //             sessionStartTime: this.state.startTime.value,
    //             sessionEndTime: this.state.endTime.value,
    //         };
    //         this.props.createClassRoomSession(classRoomDataParams, (res) => {
    //             this.setState({ isLoading: false });
    //             if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
    //                 this.props.showNotification(res.message, 'success');
    //                 this.props.history.push(
    //                     `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
    //                 );
    //             } else {
    //                 this.props.showNotification(res.message, 'error');
    //             }
    //         });
    //     }
    // };

    handleSubmit = () => {
        const isHealthCare = AppConfig?.isHealthCare === true;

        const {
            subjectId,
            subCategoryId,
            sessionLanguage,
            date,
            timeApp,
            isInPerson,
            isOneToOne,
            conference,
            isFromStudent,
            studentId,
            studentIds,
            comments,
            summary,
            uploadedImage,
            startTime,
            endTime,
            role,
            selectedChannelEmail,
            levelId,
            invitePatient,
            emails,
            currentEmail,
            isRescheduleMode,
            rescheduleVisitId,
        } = this.state;

        const { userDetails } = this.props;
        const tutorId = this.getTutorId();
        const hasRescheduleDateTime = date && startTime && endTime;
        // ---------------- VALIDATIONS ----------------
        if (!subjectId) {
            this.props.showNotification(
                RESPONSE_CONSTANT.SUBJECT_MANDATORY,
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        if (
            (isRescheduleMode && !hasRescheduleDateTime) ||
            (!isRescheduleMode && (!date || !timeApp.length))
        ) {
            this.props.showNotification(
                RESPONSE_CONSTANT.SELECT_DATE_TIME,
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        if (startTime && endTime) {
            const selectedStartTime = moment(startTime.value);
            const selectedEndTime = moment(endTime.value);

            if (!selectedStartTime.isBefore(selectedEndTime)) {
                this.props.showNotification(
                    RESPONSE_CONSTANT.START_TIME_ALERT,
                    RESPONSE_CONSTANT.WARNING
                );
                return;
            }
        }

        if (!isInPerson && !conference) {
            if (!isHealthCare) {
                // Non-healthcare
                if (
                    (isOneToOne && !studentId) ||
                    (!isOneToOne && (!studentIds || !studentIds.length))
                ) {
                    this.props.showNotification(
                        RESPONSE_CONSTANT.STUDENT_MANDATORY,
                        RESPONSE_CONSTANT.WARNING
                    );
                    return;
                }
            } else {
                // Healthcare
                // One-to-One
                if (isOneToOne) {
                    if (!invitePatient && !studentId) {
                        this.props.showNotification(
                            RESPONSE_CONSTANT.STUDENT_MANDATORY,
                            RESPONSE_CONSTANT.WARNING
                        );
                        return;
                    }
                    // InvitePatient ON → optional, no error
                } else {
                    // Group / Classroom
                    if (!studentIds || !studentIds.length) {
                        this.props.showNotification(
                            RESPONSE_CONSTANT.STUDENT_MANDATORY,
                            RESPONSE_CONSTANT.WARNING
                        );
                        return;
                    }
                    // Group/classroom always requires at least one team member.
                }
            }
        }

        if (conference && !emails.length) {
            this.props.showNotification(
                RESPONSE_CONSTANT.ENTER_EMAIL_ADDRESS,
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        const pendingConferenceEmail = String(currentEmail || '')
            .trim()
            .toLowerCase();
        const conferenceEmailsForValidation = toUniqueNormalizedEmails([
            ...emails,
            ...(pendingConferenceEmail && validateEmail(pendingConferenceEmail)
                ? [pendingConferenceEmail]
                : []),
        ]);
        const invitePatientEmailsForValidation = invitePatient
            ? toUniqueNormalizedEmails(selectedChannelEmail)
            : [];
        const overlappingExternalEmails = conference
            ? getOverlappingEmails(conferenceEmailsForValidation, invitePatientEmailsForValidation)
            : [];

        if (overlappingExternalEmails.length) {
            this.props.showNotification(
                this.props.t('overlappingExternalEmailsError') ||
                    'Same email cannot be used in both Invite Patient and Enter Email. Please keep it in only one section before saving.',
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        if (!tutorId) {
            this.props.showNotification(
                'Tutor information is missing. Please refresh the page.',
                RESPONSE_CONSTANT.ERROR
            );
            return;
        }

        // ---------------- VISIT TYPE ----------------
        const VISIT_TYPE = {
            ONE_TO_ONE: 2,
            GROUP: 4,
            IN_PERSON: 6,
            CONFERENCE: 8,
        };

        const getVisitType = () => {
            if (conference) return VISIT_TYPE.CONFERENCE;
            if (isInPerson) return VISIT_TYPE.IN_PERSON;
            if (isOneToOne) return VISIT_TYPE.ONE_TO_ONE;
            return VISIT_TYPE.GROUP;
        };

        const visitType = getVisitType();
        this.setState({ isLoading: true });

        // ---------------- ONE TO ONE / IN PERSON FLOW ----------------
        if (isOneToOne || isFromStudent || isInPerson) {
            const selectedStudentIsOwner = isTrueValue(studentId?.owner);
            const data = {
                sessionDate: moment(date).format('YYYY-MM-DD'),
                expertiseId: subCategoryId?.value || null,
                expertiseCategoryId: subjectId?.value || null,
                comments,
                visitType,
                ...(isHealthCare
                    ? {
                          transcriptionLanguage:
                              sessionLanguage?.value || DEFAULT_TRANSCRIPTION_LANGUAGE,
                      }
                    : {}),
                educatorInsId: userDetails.associatedLevels[0].id,
                tutorId,
                studentId: studentId?.value ?? studentId?.id,
                topic: summary,
                referenceFiles: uploadedImage,
                sessionStartTime: startTime.value,
                sessionEndTime: endTime.value,
                ownerToOwner: selectedStudentIsOwner || isTrueValue(role),
                selectedStudentIsOwner,
                ...(conference
                    ? {
                          externalUserEmails: [
                              ...emails,
                              ...(invitePatient && selectedChannelEmail
                                  ? Array.isArray(selectedChannelEmail)
                                      ? selectedChannelEmail
                                      : [selectedChannelEmail]
                                  : []),
                          ],
                      }
                    : isInPerson
                      ? { inPersonEmail: selectedChannelEmail }
                      : invitePatient
                        ? {
                              externalUserEmails: Array.isArray(selectedChannelEmail)
                                  ? selectedChannelEmail
                                  : selectedChannelEmail
                                    ? [selectedChannelEmail]
                                    : [],
                          }
                        : {}),
            };

            if (isRescheduleMode && rescheduleVisitId) {
                this.submitRescheduleUpdate(data);
                return;
            }

            this.props.appointmentBook(data, (res) => {
                this.setState({ isLoading: false });
                if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                    this.props.showNotification(res.message, 'success');
                    this.props.history.push(
                        `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
                    );
                } else {
                    this.props.showNotification(res.message, 'error');
                }
            });
            return;
        }

        // ---------------- CLASSROOM / GROUP FLOW ----------------
        const studentSelection = Array.isArray(studentIds) ? studentIds : [];
        const studentIdList = studentSelection
            .filter((item) => !isTrueValue(item?.owner))
            .map((item) => String(item?.id || item?.value || '').trim())
            .filter(Boolean);
        const ownerIdList = studentSelection
            .filter((item) => isTrueValue(item?.owner))
            .map((item) => String(item?.id || item?.value || '').trim())
            .filter(Boolean);

        const classRoomDataParams = {
            sessionDate: moment(date).format('YYYY-MM-DD'),
            expertiseId: subCategoryId?.value || null,
            expertiseCategoryId: subjectId.value,
            slotIds: store.get('slotIds'),
            comments,
            visitType,
            ...(isHealthCare
                ? {
                      transcriptionLanguage:
                          sessionLanguage?.value || DEFAULT_TRANSCRIPTION_LANGUAGE,
                  }
                : {}),
            educatorInsId: levelId.value,
            educatorId: tutorId,
            studentIds: studentIdList,
            ownerIds: ownerIdList,
            topic: summary,
            referenceFiles: uploadedImage,
            sessionStartTime: startTime.value,
            sessionEndTime: endTime.value,
            ...(conference
                ? {
                      externalUserEmails: [
                          ...emails,
                          ...(invitePatient && selectedChannelEmail
                              ? Array.isArray(selectedChannelEmail)
                                  ? selectedChannelEmail
                                  : [selectedChannelEmail]
                              : []),
                      ],
                  }
                : isInPerson
                  ? { inPersonEmail: selectedChannelEmail }
                  : invitePatient
                    ? {
                          externalUserEmails: Array.isArray(selectedChannelEmail)
                              ? selectedChannelEmail
                              : selectedChannelEmail
                                ? [selectedChannelEmail]
                                : [],
                      }
                    : {}),
        };

        if (isRescheduleMode && rescheduleVisitId) {
            this.submitRescheduleUpdate(classRoomDataParams);
            return;
        }

        this.props.createClassRoomSession(classRoomDataParams, (res) => {
            this.setState({ isLoading: false });
            if (res.code === RESPONSE_CONSTANT.SUCCESS_CODE) {
                this.props.showNotification(res.message, 'success');
                this.props.history.push(
                    `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
                );
            } else {
                this.props.showNotification(res.message, 'error');
            }
        });
    };

    // filterDates
    filterDates = (date) => {
        return true;
    };

    handleInputChange = (e) => {
        this.setState({ currentEmail: e.target.value });
    };

    handleInvitePatientToggle = (checked) => {
        if (this.isPrefilledValueLockEnabled()) {
            this.props.showNotification(
                'Started session values cannot be changed.',
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        this.setState((prevState) => {
            const isGroupRescheduleFlow = !prevState.isOneToOne && !prevState.isInPerson;
            const shouldPreservePrefilledParticipants =
                prevState.isRescheduleMode &&
                prevState.hasAppliedReschedulePrefill &&
                (prevState.conference || isGroupRescheduleFlow);

            if (shouldPreservePrefilledParticipants) {
                return {
                    invitePatient: checked,
                    invitePatientCachedStudentId: '',
                    invitePatientCachedStudentIds: [],
                };
            }

            const shouldClearParticipants =
                prevState.isOneToOne || prevState.isFromStudent || prevState.conference;

            if (checked) {
                if (!shouldClearParticipants) {
                    return {
                        invitePatient: true,
                        invitePatientCachedStudentId: '',
                        invitePatientCachedStudentIds: [],
                    };
                }

                return {
                    invitePatient: true,
                    invitePatientCachedStudentId: prevState.studentId || '',
                    invitePatientCachedStudentIds: Array.isArray(prevState.studentIds)
                        ? [...prevState.studentIds]
                        : [],
                    studentId: '',
                    studentIds: [],
                };
            }

            if (!shouldClearParticipants) {
                return {
                    invitePatient: false,
                    invitePatientCachedStudentId: '',
                    invitePatientCachedStudentIds: [],
                };
            }

            const shouldRestoreSingleSelection =
                prevState.isOneToOne ||
                prevState.isFromStudent ||
                !Array.isArray(prevState.studentIds);

            return {
                invitePatient: false,
                studentId: shouldRestoreSingleSelection
                    ? prevState.invitePatientCachedStudentId || prevState.studentId
                    : prevState.studentId,
                studentIds: shouldRestoreSingleSelection
                    ? prevState.studentIds
                    : Array.isArray(prevState.invitePatientCachedStudentIds)
                      ? [...prevState.invitePatientCachedStudentIds]
                      : prevState.studentIds,
                invitePatientCachedStudentId: '',
                invitePatientCachedStudentIds: [],
            };
        });
    };

    handleAddEmail = () => {
        const { emails, currentEmail } = this.state;
        const email = currentEmail.trim();

        // 1. Required
        if (!email) {
            return this.props.showNotification(
                RESPONSE_CONSTANT.ENTER_EMAIL_ADDRESS,
                RESPONSE_CONSTANT.WARNING
            );
        }

        // 2. Limit check FIRST (important fix)
        if (emails.length >= MAX_EMAILS) {
            return this.props.showNotification(
                formatMessage(RESPONSE_CONSTANT.MAX_EMAIL_LIMIT, { count: MAX_EMAILS }),
                RESPONSE_CONSTANT.WARNING
            );
        }

        // 3. Format validation
        if (!validateEmail(email)) {
            return this.props.showNotification(
                formatMessage(RESPONSE_CONSTANT.INVALID_EMAIL),
                RESPONSE_CONSTANT.WARNING
            );
        }

        // 4. Normalize + duplicate check
        const normalizedEmail = email.toLowerCase();

        if (emails.some((e) => e.toLowerCase() === normalizedEmail)) {
            return this.props.showNotification(
                RESPONSE_CONSTANT.EMAIL_ALREADY_ADDED,
                RESPONSE_CONSTANT.WARNING
            );
        }

        // 5. Success
        this.setState((prevState) => ({
            emails: [...prevState.emails, normalizedEmail],
            currentEmail: '',
        }));
    };

    handleRemoveEmail = (index) => {
        const emails = [...this.state.emails];
        const emailToRemove = emails[index];

        if (this.isPrefilledValueLockEnabled() && this.isLockedPrefilledEmail(emailToRemove)) {
            this.props.showNotification(
                'Started session values cannot be removed.',
                RESPONSE_CONSTANT.WARNING
            );
            return;
        }

        emails.splice(index, 1);
        this.setState({ emails });
    };

    /**
     *  Render Page
     */

    customOptionLabel = ({ label, activeStatus }) => (
        <div className="flex items-center gap-2 overflow-hidden">
            <span
                title={activeStatus === 'AVAILABLE' ? 'Available' : 'Busy'}
                className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    activeStatus === 'AVAILABLE' ? 'bg-green-500' : 'bg-red-500'
                }`}
            />
            <span className="truncate" title={label}>
                {label}
            </span>
        </div>
    );

    render() {
        const {
            //  associatedLevels,
            createdRole,
        } = this.props.userDetails;
        const { subjectList, emails, currentEmail, isRescheduleMode, isReschedulePrefillPending } =
            this.state;
        const currentTime = moment();
        const profileTimezone = this.getProfileTimezone();
        // Slot times are wall-clock times in the profile timezone (shown and sent as returned)
        const startOptions = this.state.timeSlots
            .filter((slot) => moment.tz(slot.startTime, profileTimezone).isSameOrAfter(currentTime))
            .map((slot) => ({
                value: slot.startTime,
                label: moment(slot.startTime).format('hh:mm A'),
            }));
        let endOptions = [];
        if (this.state.startTime) {
            const selectedStartTime = moment(this.state.startTime.value);
            endOptions = this.state.timeSlots
                .filter(
                    (slot) =>
                        moment(slot.startTime).isSame(selectedStartTime) ||
                        moment(slot.startTime).isAfter(selectedStartTime)
                )
                .map((slot) => ({
                    value: slot.endTime,
                    label: moment(slot.endTime).format('hh:mm A'),
                }));
        }

        const showEmailPill = AppConfig.isHealthCare && !!this.state.selectedChannelEmail;

        const showInviteToggle =
            AppConfig.isHealthCare && !!this.state.selectedChannelEmail && !this.state.isInPerson; // ❗ Only One-to-One & Group
        const isHealthCare = AppConfig?.isHealthCare === true;
        // const isCoreDomain = AppConfig?.isHealthCare === false;
        const isConferenceSession = this.state.conference;
        const activeSessionType = this.state.isInPerson
            ? 'inperson'
            : isConferenceSession
              ? 'conference'
              : this.state.isOneToOne
                ? 'onetoone'
                : 'classroom';
        const isSessionTypeLocked =
            isRescheduleMode &&
            !isReschedulePrefillPending &&
            this.state.hasAppliedReschedulePrefill;
        const isSessionTypeDisabled = (type) => isSessionTypeLocked && activeSessionType !== type;
        const isPrefilledValueLocked = this.isPrefilledValueLockEnabled();
        const lockedReadOnlySelectStyles = isPrefilledValueLocked
            ? {
                  ...customStyles,
                  control: (provided, state) => ({
                      ...(typeof customStyles.control === 'function'
                          ? customStyles.control(provided, state)
                          : provided),
                      backgroundColor: '#34373E',
                      borderColor: '#4B5563',
                      opacity: 1,
                      cursor: 'not-allowed',
                  }),
                  singleValue: (provided, state) => ({
                      ...(typeof customStyles.singleValue === 'function'
                          ? customStyles.singleValue(provided, state)
                          : provided),
                      color: '#E5E7EB',
                  }),
                  placeholder: (provided, state) => ({
                      ...(typeof customStyles.placeholder === 'function'
                          ? customStyles.placeholder(provided, state)
                          : provided),
                      color: '#9CA3AF',
                  }),
                  input: (provided, state) => ({
                      ...(typeof customStyles.input === 'function'
                          ? customStyles.input(provided, state)
                          : provided),
                      color: '#E5E7EB',
                  }),
              }
            : customStyles;
        const lockedChannelSelectStyles = lockedReadOnlySelectStyles;
        const lockedStudentKeySet = new Set(this.state.lockedPrefilledStudentKeys || []);
        const resolveSelectStyle = (styleEntry, provided, state) =>
            typeof styleEntry === 'function' ? styleEntry(provided, state) : provided;
        const participantSelectStyles = {
            ...customStyles,
            control: (provided, state) => {
                const baseStyle = resolveSelectStyle(customStyles.control, provided, state);
                const shouldUseLockedControlStyle =
                    isPrefilledValueLocked && isOneToOneParticipantLocked;

                if (!shouldUseLockedControlStyle) {
                    return baseStyle;
                }

                return {
                    ...baseStyle,
                    backgroundColor: '#34373E',
                    borderColor: '#4B5563',
                    opacity: 1,
                };
            },
            multiValue: (provided, state) => {
                const baseStyle = resolveSelectStyle(customStyles.multiValue, provided, state);
                const isLocked =
                    isPrefilledValueLocked &&
                    lockedStudentKeySet.has(getStudentOptionKey(state.data));

                if (!isLocked) {
                    return baseStyle;
                }

                return {
                    ...baseStyle,
                    backgroundColor: '#4B5563',
                    opacity: 1,
                };
            },
            multiValueLabel: (provided, state) => {
                const baseStyle = resolveSelectStyle(customStyles.multiValueLabel, provided, state);
                const isLocked =
                    isPrefilledValueLocked &&
                    lockedStudentKeySet.has(getStudentOptionKey(state.data));

                return isLocked
                    ? {
                          ...baseStyle,
                          color: '#E5E7EB',
                      }
                    : baseStyle;
            },
            multiValueRemove: (provided, state) => {
                const baseStyle = resolveSelectStyle(
                    customStyles.multiValueRemove,
                    provided,
                    state
                );
                const isLocked =
                    isPrefilledValueLocked &&
                    lockedStudentKeySet.has(getStudentOptionKey(state.data));

                return isLocked
                    ? {
                          ...baseStyle,
                          display: 'none',
                      }
                    : baseStyle;
            },
            singleValue: (provided, state) => {
                const baseStyle = resolveSelectStyle(customStyles.singleValue, provided, state);
                const selectedKey = getStudentOptionKey(this.state.studentId);
                const isLocked =
                    isPrefilledValueLocked && selectedKey && lockedStudentKeySet.has(selectedKey);

                return isLocked
                    ? {
                          ...baseStyle,
                          color: '#E5E7EB',
                      }
                    : baseStyle;
            },
        };
        const isOneToOneParticipantLocked =
            isPrefilledValueLocked &&
            !!this.state.studentId &&
            (this.state.lockedPrefilledStudentKeys || []).includes(
                getStudentOptionKey(this.state.studentId)
            );
        const pageTitle = isRescheduleMode
            ? this.props.t('updateSession')
            : this.props.t('createSession');

        // Language options with translated labels; backend still receives `value` ('en'/'es').
        const languageOptions = LANGUAGE_OPTIONS.map((option) => ({
            value: option.value,
            label: this.props.t(option.value === 'es' ? 'spanish' : 'english'),
        }));
        const selectedLanguageOption =
            languageOptions.find(
                (option) => option.value === this.state.sessionLanguage?.value
            ) ||
            languageOptions.find((option) => option.value === DEFAULT_SESSION_LANGUAGE.value) ||
            null;

        return (
            <div className="fixed top-0 left-0 flex w-full h-full">
                <div
                    className={`bg-black bg-opacity-50 fixed top-0 left-0 w-full h-full z-10 ${
                        this.state.mobileNav ? 'block' : 'hidden'
                    }`}
                ></div>
                <NavigationBar
                    nav={true}
                    {...this.props}
                    active={PATH.IM_PHYSICIAN.APPOINTMENT}
                    mobileNav={this.state.mobileNav}
                    onClickMobileNav={this.onClickMobileNav}
                />
                <main className="flex flex-col flex-grow">
                    <Headers
                        {...this.props}
                        title={pageTitle}
                        onClickMobileNav={this.onClickMobileNav}
                    />
                    <div className="flex flex-col w-full h-full py-8 overflow-auto bg-UIBackground">
                        <div className="flex items-center justify-between px-4 sm:px-8">
                            <h6 className="m-0 text-lg font-semibold text-white">{pageTitle}</h6>
                        </div>
                        <div className="flex flex-row flex-grow px-4 mt-6 sm:px-8">
                            <div className="flex flex-col w-full p-6 rounded-lg bg-card">
                                <div className="flex justify-end text-[#B9B9B9] text-base font-normal">
                                    <p className="before:content-['*'] before:ml-0.5 before:text-[#10DACE] mb-3">
                                        {this.props.t('indicatesRequiredFields')}
                                    </p>
                                </div>
                                <div className="items-center justify-between sm:flex">
                                    <div className="flex mb-4 radiobox gap-x-8 sm:mb-0">
                                        {isRescheduleMode && isReschedulePrefillPending ? (
                                            <div className="text-sm text-[#B9B9B9]">
                                                {this.props.t('loading') ||
                                                    'Loading session type...'}
                                            </div>
                                        ) : this.state.isFromStudent ? (
                                            <>
                                                <div
                                                    className="flex items-center mb-4 gap-x-3 sm:mb-0"
                                                    style={{
                                                        opacity: isSessionTypeLocked ? 0.5 : 1,
                                                        cursor: isSessionTypeLocked
                                                            ? 'default'
                                                            : 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        id="one-to-one"
                                                        name="virtual"
                                                        type="radio"
                                                        className={
                                                            isSessionTypeLocked
                                                                ? 'cursor-default'
                                                                : 'cursor-pointer'
                                                        }
                                                        checked={this.state.isVirtuowl}
                                                        disabled={isSessionTypeLocked}
                                                        onChange={(e) =>
                                                            this.handleOneToOneCheckboxChange(e)
                                                        }
                                                        style={{ padding: '3px' }}
                                                    />
                                                    <label
                                                        htmlFor="select-one-one"
                                                        className={`block text-base ${
                                                            this.state.isVirtuowl
                                                                ? 'font-semibold'
                                                                : 'font-normal'
                                                        } leading-6 text-white`}
                                                    >
                                                        {this.props.t('appointment')}
                                                    </label>
                                                </div>
                                                {/* {AppConfig.isHealthCare && (
                                                    <div className="flex items-center gap-x-3">
                                                        <input
                                                            id="inperson-student"
                                                            name="inperson"
                                                            type="radio"
                                                            className="cursor-pointer"
                                                            checked={this.state.isInPerson}
                                                            onChange={
                                                                this.handleOneToOneCheckboxChange
                                                            }
                                                            style={{ padding: '3px' }}
                                                        />
                                                        <label
                                                            htmlFor="inperson-student"
                                                            className={`block text-base ${
                                                                this.state.isInPerson
                                                                    ? 'font-semibold'
                                                                    : 'font-normal'
                                                            } leading-6 text-white`}
                                                        >
                                                            In Person
                                                        </label>
                                                    </div>
                                                )} */}
                                            </>
                                        ) : (
                                            <>
                                                <div
                                                    className="flex items-center mb-4 gap-x-3 sm:mb-0"
                                                    style={{
                                                        opacity: isSessionTypeDisabled('onetoone')
                                                            ? 0.5
                                                            : 1,
                                                        cursor: isSessionTypeDisabled('onetoone')
                                                            ? 'default'
                                                            : 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        id="one-to-one"
                                                        name="onetoone"
                                                        type="radio"
                                                        className={
                                                            isSessionTypeDisabled('onetoone')
                                                                ? 'cursor-default'
                                                                : 'cursor-pointer'
                                                        }
                                                        checked={this.state.isOneToOne}
                                                        disabled={isSessionTypeDisabled('onetoone')}
                                                        onChange={(e) =>
                                                            this.handleOneToOneCheckboxChange(e)
                                                        }
                                                        style={{ padding: '3px' }}
                                                    />
                                                    <label
                                                        htmlFor="select-one-one"
                                                        className={`block text-base ${
                                                            this.state.isOneToOne
                                                                ? 'font-normal'
                                                                : 'font-normal'
                                                        } leading-6 text-white peer-checked:ring-2 peer-checked:ring-customteal`}
                                                    >
                                                        {this.props.t('oneToOne')}
                                                    </label>
                                                </div>

                                                <div
                                                    className="flex items-center gap-x-3"
                                                    style={{
                                                        opacity: isSessionTypeDisabled('classroom')
                                                            ? 0.5
                                                            : 1,
                                                        cursor: isSessionTypeDisabled('classroom')
                                                            ? 'default'
                                                            : 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        id="classroom"
                                                        name="classroom"
                                                        type="radio"
                                                        className={
                                                            isSessionTypeDisabled('classroom')
                                                                ? 'cursor-default'
                                                                : 'cursor-pointer'
                                                        }
                                                        checked={
                                                            !this.state.isOneToOne &&
                                                            !this.state.isInPerson &&
                                                            !isConferenceSession
                                                        }
                                                        disabled={isSessionTypeDisabled(
                                                            'classroom'
                                                        )}
                                                        onChange={(e) =>
                                                            this.handleOneToOneCheckboxChange(e)
                                                        }
                                                        style={{ padding: '3px' }}
                                                    />
                                                    <label
                                                        htmlFor="select-classroom"
                                                        className={`block text-base ${
                                                            !this.state.isOneToOne
                                                                ? 'font-normal'
                                                                : 'font-normal'
                                                        } leading-6 text-white peer-checked:ring-2 peer-checked:ring-customteal`}
                                                    >
                                                        {this.props.t('groupSession')}
                                                    </label>
                                                </div>
                                                {/* {isCoreDomain && ( */}
                                                <div
                                                    className="flex items-center mt-4 gap-x-3 sm:mt-0"
                                                    style={{
                                                        opacity: isSessionTypeDisabled('conference')
                                                            ? 0.5
                                                            : 1,
                                                        cursor: isSessionTypeDisabled('conference')
                                                            ? 'default'
                                                            : 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        id="conference"
                                                        name="conference"
                                                        type="radio"
                                                        className={
                                                            isSessionTypeDisabled('conference')
                                                                ? 'cursor-default'
                                                                : 'cursor-pointer'
                                                        }
                                                        checked={isConferenceSession}
                                                        disabled={isSessionTypeDisabled(
                                                            'conference'
                                                        )}
                                                        onChange={(e) =>
                                                            this.handleOneToOneCheckboxChange(e)
                                                        }
                                                        style={{ padding: '3px' }}
                                                    />
                                                    <label
                                                        htmlFor="conference"
                                                        className={`block text-base ${
                                                            isConferenceSession
                                                                ? 'font-semibold'
                                                                : 'font-normal'
                                                        } leading-6 text-white`}
                                                    >
                                                        {this.props.t('conferenceSession')}
                                                    </label>
                                                </div>
                                                {/* )} */}
                                                <div
                                                    className="flex items-center mt-4 gap-x-3 sm:mt-0"
                                                    style={{
                                                        opacity: isSessionTypeDisabled('inperson')
                                                            ? 0.5
                                                            : 1,
                                                        cursor: isSessionTypeDisabled('inperson')
                                                            ? 'default'
                                                            : 'pointer',
                                                    }}
                                                >
                                                    <input
                                                        id="inperson-teacher"
                                                        name="inperson"
                                                        type="radio"
                                                        className={
                                                            isSessionTypeDisabled('inperson')
                                                                ? 'cursor-default'
                                                                : 'cursor-pointer'
                                                        }
                                                        checked={this.state.isInPerson}
                                                        disabled={isSessionTypeDisabled('inperson')}
                                                        onChange={this.handleOneToOneCheckboxChange}
                                                        style={{ padding: '3px' }}
                                                    />
                                                    <label
                                                        htmlFor="inperson-teacher"
                                                        className={`block text-base ${
                                                            this.state.isInPerson
                                                                ? 'font-semibold'
                                                                : 'font-normal'
                                                        } leading-6 text-white`}
                                                    >
                                                        {this.props.t('inPerson')}
                                                    </label>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-12">
                                    <div className="col-span-10 mt-3 sm:col-span-10">
                                        {createdRole !== RESPONSE_CONSTANT.BRANCHADMIN ? (
                                            <div className="grid sm:grid-cols-1">
                                                {/* <div>
                          <label
                            htmlFor="select-subject"
                            className="block mb-2 text-white font-normal text-white mt-6 after:content-['*'] after:ml-0.5 after:text-red"
                          >
                            Select Level 3
                          </label>
                          <Select
                            isLoading={this.state.isLevelLoading}
                            isDisabled={this.state.isFromStudent}
                            id="level"
                            name="level"
                            onChange={(event) => {
                              this.setState({
                                levelId: event,
                                subjectId: "",
                                subCategoryId: "",
                                date: "",
                                timeApp: [],
                                studentId: "",
                                studentIds: [],
                                summary: "",
                                studentList: [],
                                uploadedImage: [],
                                startTime: "",
                                endTime: "",
                              });
                            }}
                            value={this.state.levelId}
                            ref={(r) => (this.LevelRef = r)}
                            className="block text-white rounded text-md bg-card whitespace-nowrap"
                            options={
                              associatedLevels &&
                              associatedLevels.length &&
                              associatedLevels
                            }
                            styles={customStyles}
                            isSearchable={true}
                            placeholder="Select any level"
                            onKeyDown={(event) => {
                              const key = event.key;
                              const allowedCharacters = /^[a-zA-Z0-9\s]+$/;
                              if (
                                !allowedCharacters.test(key) ||
                                (event.keyCode === 32 &&
                                  !this.LevelRef.state.inputValue)
                              ) {
                                event.preventDefault();
                              }
                            }}
                            formatOptionLabel={this.customOptionLabel}
                          />
                        </div> */}
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div>
                                                <label
                                                    htmlFor="select-subject"
                                                    className="block mb-2 text-white font-normal mt-6 after:content-['*'] after:ml-0.5 after:text-[#10DACE]"
                                                >
                                                    {this.props.t('select')}{' '}
                                                    {this.props
                                                        .t(AppConfig.labels.channelName)
                                                        .toLowerCase()}
                                                </label>
                                                <Select
                                                    isLoading={this.state.isSubjectLoading}
                                                    id="subject"
                                                    name="subject"
                                                    isDisabled={isPrefilledValueLocked}
                                                    onChange={(event) => {
                                                        const keepPrefilledValues =
                                                            this.state.isRescheduleMode &&
                                                            this.state.hasAppliedReschedulePrefill;

                                                        this.setState({
                                                            selectedChannelEmail:
                                                                event?.inPersonEmail || '',
                                                            subjectId: event,
                                                            subCategoryId: '',
                                                            ...(keepPrefilledValues
                                                                ? {}
                                                                : {
                                                                      date: '',
                                                                      timeApp: [],
                                                                      studentId: !this.state
                                                                          .isFromStudent
                                                                          ? ''
                                                                          : this.state.studentId,
                                                                      studentIds: [],
                                                                      summary: '',
                                                                      studentList: [],
                                                                      uploadedImage: [],
                                                                      startTime: '',
                                                                      endTime: '',
                                                                  }),
                                                        });
                                                        this.getEducatorSubCategoryList(
                                                            event.value
                                                        );
                                                    }}
                                                    value={this.state.subjectId}
                                                    ref={(r) => (this.subjectRef = r)}
                                                    className="block text-white rounded text-md whitespace-nowrap"
                                                    options={
                                                        subjectList && subjectList.length
                                                            ? subjectList
                                                            : []
                                                    }
                                                    styles={lockedChannelSelectStyles}
                                                    isSearchable={true}
                                                    placeholder={`${this.props.t('select')} ${this.props.t(AppConfig.labels.channelName)}`}
                                                    noOptionsMessage={() =>
                                                        this.props.t('noOptions')
                                                    }
                                                    onKeyDown={(event) => {
                                                        const key = event.key;
                                                        const allowedCharacters =
                                                            /^[a-zA-Z0-9\s]+$/;
                                                        if (
                                                            !allowedCharacters.test(key) ||
                                                            (event.keyCode === 32 &&
                                                                !this.subjectRef.state.inputValue)
                                                        ) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {showEmailPill && (
                                                    <div
                                                        className={`mt-3 inline-flex items-center gap-x-3 px-4 py-2 rounded-full border ${
                                                            isPrefilledValueLocked
                                                                ? 'bg-[#101317] border-[#4B5563] opacity-70'
                                                                : 'bg-[#181B22] border-[#374151]'
                                                        }`}
                                                    >
                                                        <span
                                                            className="text-sm text-gray-300 truncate max-w-[220px]"
                                                            title={this.state.selectedChannelEmail}
                                                        >
                                                            {this.state.selectedChannelEmail}
                                                        </span>

                                                        {isHealthCare && showInviteToggle && (
                                                            <label
                                                                className={`relative inline-flex items-center ${
                                                                    isPrefilledValueLocked
                                                                        ? 'cursor-not-allowed'
                                                                        : 'cursor-pointer'
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        this.state.invitePatient
                                                                    }
                                                                    disabled={
                                                                        isPrefilledValueLocked
                                                                    }
                                                                    onChange={(e) =>
                                                                        this.handleInvitePatientToggle(
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                    className="sr-only peer"
                                                                />
                                                                <div
                                                                    className={`w-9 h-5 rounded-full transition-colors ${
                                                                        isPrefilledValueLocked
                                                                            ? 'opacity-60'
                                                                            : ''
                                                                    }
    ${
        isPrefilledValueLocked
            ? 'bg-[#2F3440]'
            : this.state.invitePatient
              ? 'bg-[#543CC2]'
              : 'bg-[#374151]'
    }
    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
    after:rounded-full after:h-4 after:w-4 after:transition-all
    ${this.state.invitePatient ? 'after:translate-x-4' : ''}
    ${
        isPrefilledValueLocked
            ? 'after:bg-[#9CA3AF]'
            : this.state.invitePatient
              ? 'after:bg-white'
              : 'after:bg-[#9CA3AF]'
    }
  `}
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="select-Element"
                                                    className="block mt-6 mb-2 text-base font-normal text-white rounded"
                                                >
                                                    {this.props.t('selectElement')}
                                                </label>
                                                <Select
                                                    isLoading={this.state.isSubcategoryLoading}
                                                    id="subcategory"
                                                    name="subcategory"
                                                    isDisabled={isPrefilledValueLocked}
                                                    onChange={(event) => {
                                                        const keepPrefilledValues =
                                                            this.state.isRescheduleMode &&
                                                            this.state.hasAppliedReschedulePrefill;

                                                        this.setState({
                                                            subCategoryId: event,
                                                            ...(keepPrefilledValues
                                                                ? {}
                                                                : {
                                                                      date: '',
                                                                      timeApp: [],
                                                                      studentIds: [],
                                                                      studentId: !this.state
                                                                          .isFromStudent
                                                                          ? ''
                                                                          : this.state.studentId,
                                                                      summary: '',
                                                                      studentList: [],
                                                                      uploadedImage: [],
                                                                      startTime: '',
                                                                      endTime: '',
                                                                  }),
                                                        });
                                                    }}
                                                    value={this.state.subCategoryId}
                                                    ref={(r) => (this.subcategoryRef = r)}
                                                    className="block rounded text-secondary text-md border-black-10 whitespace-nowrap subcategory-dropdown"
                                                    options={
                                                        this.state.subCategoryList &&
                                                        this.state.subCategoryList.length
                                                            ? this.state.subCategoryList
                                                            : []
                                                    }
                                                    styles={lockedReadOnlySelectStyles}
                                                    isSearchable={true}
                                                    placeholder={this.props.t('selectElement')}
                                                    noOptionsMessage={() =>
                                                        this.props.t('noOptions')
                                                    }
                                                    onKeyDown={(event) => {
                                                        const key = event.key;
                                                        const allowedCharacters =
                                                            /^[a-zA-Z0-9\s]+$/;
                                                        if (
                                                            !allowedCharacters.test(key) ||
                                                            (event.keyCode === 32 &&
                                                                !this.subcategoryRef.state
                                                                    .inputValue)
                                                        ) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {isHealthCare && (
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label
                                                        htmlFor="select-language"
                                                        className="block mb-2 text-base font-normal text-white rounded mt-6"
                                                    >
                                                        {this.props.t('language')}
                                                    </label>
                                                    <Select
                                                        id="language"
                                                        name="language"
                                                        isDisabled={isPrefilledValueLocked}
                                                        onChange={(event) => {
                                                            this.setState({
                                                                sessionLanguage: event
                                                                    ? {
                                                                          value: event.value,
                                                                          label: event.label,
                                                                      }
                                                                    : DEFAULT_SESSION_LANGUAGE,
                                                            });
                                                        }}
                                                        value={selectedLanguageOption}
                                                        className="block rounded text-secondary text-md border-black-10 whitespace-nowrap"
                                                        options={languageOptions}
                                                        styles={lockedReadOnlySelectStyles}
                                                        isSearchable={false}
                                                        placeholder={this.props.t(
                                                            'selectLanguage'
                                                        )}
                                                        noOptionsMessage={() =>
                                                            this.props.t('noOptions')
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="grid gap-0 sm:grid-cols-2 sm:gap-4">
                                            <div className="inputgroup">
                                                <label
                                                    htmlFor="select-date"
                                                    className="block mb-2 text-base font-normal rounded text-white mt-6 after:content-['*'] after:ml-0.5 after:text-[#10DACE]"
                                                >
                                                    {this.props.t('date')}
                                                </label>
                                                <div className="relative flex flex-wrap items-stretch w-full mb-0">
                                                    <div
                                                        className={`flex w-full mb-2 rounded ${
                                                            isPrefilledValueLocked
                                                                ? 'bg-[#34373E] cursor-default'
                                                                : 'bg-dateselect'
                                                        }`}
                                                    >
                                                        <div className="grow" style={{ paddingRight: '15px' }}>
                                                            <DatePicker
                                                                name="date"
                                                                disabled={isPrefilledValueLocked}
                                                                className={`w-full px-2 text-base text-white placeholder:text-[#B9B9B9] datepicker-opacity example-custom-input create-sessionappearance-none ${
                                                                    isPrefilledValueLocked
                                                                        ? 'text-gray-400 bg-card cursor-default locked-date-input'
                                                                        : ''
                                                                }`}
                                                                style={
                                                                    isPrefilledValueLocked
                                                                        ? {
                                                                              color: '#E5E7EB',
                                                                              backgroundColor:
                                                                                  '#34373E',
                                                                              cursor: 'default',
                                                                          }
                                                                        : undefined
                                                                }
                                                                wrapperClassName="w-full"
                                                                placeholderText={this.props.t(
                                                                    'selectDate'
                                                                )}
                                                                selected={
                                                                    this.state.date
                                                                        ? new Date(this.state.date)
                                                                        : null
                                                                }
                                                                onChange={(event) => {
                                                                    this.getAvailableSlots(event);
                                                                    const keepPrefilledValues =
                                                                        this.state
                                                                            .isRescheduleMode &&
                                                                        this.state
                                                                            .hasAppliedReschedulePrefill;

                                                                    this.setState({
                                                                        date: event,
                                                                        startDate: event,
                                                                        timeApp: [],
                                                                        startTime: '',
                                                                        endTime: '',
                                                                        ...(keepPrefilledValues
                                                                            ? {}
                                                                            : {
                                                                                  studentId: !this
                                                                                      .state
                                                                                      .isFromStudent
                                                                                      ? ''
                                                                                      : this.state
                                                                                            .studentId,
                                                                                  studentIds: [],
                                                                              }),
                                                                    });
                                                                }}
                                                                onChangeRaw={(e) => {
                                                                    this.setState({
                                                                        date: e.target.value,
                                                                    });
                                                                }}
                                                                dropdownMode="select"
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                scrollableYearDropdown
                                                                maxDate={
                                                                    new Date(
                                                                        moment(
                                                                            this.getProfileToday(),
                                                                            'DD-MM-YYYY'
                                                                        ).add(6, 'months')
                                                                    )
                                                                }
                                                                minDate={this.getProfileToday()}
                                                                autoComplete="off"
                                                                filterDate={this.filterDates}
                                                                locale={
                                                                    this.props.language === 'es'
                                                                        ? 'es'
                                                                        : 'en'
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="grid gap-0 sm:grid-cols-2 sm:gap-4"
                                                style={{ position: 'relative', zIndex: 2 }}
                                            >
                                                <div>
                                                    <label
                                                        htmlFor="select-startTime"
                                                        className="block mb-2 text-base font-normal text-white mt-6 after:content-['*'] after:ml-0.5 after:text-[#10DACE]"
                                                    >
                                                        {this.props.t('startTime')}
                                                    </label>
                                                    <div className="relative select-container">
                                                        <Select
                                                            id="startTime"
                                                            name="startTime"
                                                            value={this.state.startTime}
                                                            isDisabled={isPrefilledValueLocked}
                                                            onChange={(selectedOption) => {
                                                                const newStartTime = moment(
                                                                    selectedOption.value
                                                                );
                                                                const currentEndTime = this.state
                                                                    .endTime
                                                                    ? moment(
                                                                          this.state.endTime.value
                                                                      )
                                                                    : null;

                                                                const shouldClearEndTime =
                                                                    currentEndTime &&
                                                                    !newStartTime.isBefore(
                                                                        currentEndTime
                                                                    );

                                                                this.setState({
                                                                    startTime: selectedOption,
                                                                    ...(shouldClearEndTime
                                                                        ? {
                                                                              endTime: '',
                                                                              timeApp: [],
                                                                              myValues: [],
                                                                          }
                                                                        : {}),
                                                                });
                                                            }}
                                                            className="block text-white rounded text-md whitespace-nowrap text-content start-time hide-indicator"
                                                            options={startOptions}
                                                            placeholder="--:--"
                                                            styles={{
                                                                ...lockedReadOnlySelectStyles,
                                                                dropdownIndicator: (provided) => ({
                                                                    ...provided,
                                                                    display: 'none',
                                                                }),
                                                            }}
                                                            isSearchable={true}
                                                            noOptionsMessage={() =>
                                                                this.props.t('noOptions')
                                                            }
                                                            onKeyDown={(event) => {
                                                                const key = event.key;
                                                                const allowedCharacters =
                                                                    /^[a-zA-Z0-9\s:]+$/;
                                                                if (
                                                                    !allowedCharacters.test(key) ||
                                                                    (event.keyCode === 32 &&
                                                                        !this.selectRef.state
                                                                            .inputValue)
                                                                ) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <i className="fa-regular fa-clock absolute top-3 right-3 z-10 text-[#10DACE]"></i>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label
                                                        htmlFor="select-endTime"
                                                        className="block mb-2 text-base font-normal text-white mt-6 after:content-['*'] after:ml-0.5 after:text-[#10DACE]"
                                                    >
                                                        {this.props.t('endTime')}
                                                    </label>
                                                    <div
                                                        className="select-container"
                                                        style={{ position: 'relative' }}
                                                    >
                                                        <Select
                                                            id="endTime"
                                                            name="endTime"
                                                            value={this.state.endTime}
                                                            isDisabled={isPrefilledValueLocked}
                                                            onChange={(selectedOption) => {
                                                                const selectedSlot =
                                                                    this.state.timeSlots.find(
                                                                        (slot) =>
                                                                            slot.endTime ===
                                                                            selectedOption.value
                                                                    );
                                                                const selectedSlotId = selectedSlot
                                                                    ? selectedSlot.id
                                                                    : null;
                                                                const updatedTimeApp = [
                                                                    ...this.state.timeApp,
                                                                    { slotId: selectedSlotId },
                                                                ];

                                                                this.setState(
                                                                    {
                                                                        endTime: selectedOption,
                                                                        timeApp: updatedTimeApp,
                                                                    },
                                                                    () => {
                                                                        this.getAvailableStudentList();
                                                                    }
                                                                );
                                                            }}
                                                            className="block rounded text-secondary text-md border-black-10 whitespace-nowrap text-content"
                                                            options={endOptions}
                                                            placeholder="--:--"
                                                            styles={{
                                                                ...lockedReadOnlySelectStyles,
                                                                dropdownIndicator: (provided) => ({
                                                                    ...provided,
                                                                    display: 'none',
                                                                }),
                                                            }}
                                                            isSearchable={true}
                                                            noOptionsMessage={() =>
                                                                this.props.t('noOptions')
                                                            }
                                                            onKeyDown={(event) => {
                                                                const key = event.key;
                                                                const allowedCharacters =
                                                                    /^[a-zA-Z0-9\s:]+$/;
                                                                if (
                                                                    !allowedCharacters.test(key) ||
                                                                    (event.keyCode === 32 &&
                                                                        !this.selectRef.state
                                                                            .inputValue)
                                                                ) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <i className="fa-regular fa-clock absolute top-3 right-3 z-10 text-[#10DACE]"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {(!this.state.isInPerson ||
                                            this.state.isFromStudent ||
                                            isConferenceSession) && (
                                            <div className="grid grid-cols-1">
                                                <div>
                                                    <label className="block mt-6 mb-2 text-base font-normal text-white">
                                                        {this.state.isOneToOne ||
                                                        this.state.isFromStudent
                                                            ? this.props.t('selectTeamMember')
                                                            : this.props.t('selectTeamMembers')}

                                                        {/* ✅ Mandatory star logic */}
                                                        {!isConferenceSession &&
                                                            (isHealthCare ? (
                                                                !this.state.isOneToOne ? (
                                                                    <span className="after:content-['*'] after:ml-0.5 after:text-[#10DACE]" />
                                                                ) : !this.state.invitePatient ? (
                                                                    <span className="after:content-['*'] after:ml-0.5 after:text-[#10DACE]" />
                                                                ) : null
                                                            ) : (
                                                                <span className="after:content-['*'] after:ml-0.5 after:text-[#10DACE]" />
                                                            ))}
                                                    </label>

                                                    {/* One-to-One or Student-specific select */}
                                                    {this.state.isOneToOne ||
                                                    this.state.isFromStudent ? (
                                                        <Select
                                                            isLoading={this.state.isStudentLoading}
                                                            isDisabled={
                                                                (!isConferenceSession &&
                                                                    this.state.invitePatient) ||
                                                                this.state.isFromStudent ||
                                                                isOneToOneParticipantLocked
                                                            } // Only disable if "fromStudent"
                                                            ref={(r) => (this.selectRef = r)}
                                                            className="block rounded text-secondary text-md border-black-10 whitespace-nowrap"
                                                            name="student"
                                                            onChange={(event) => {
                                                                const isSelectedOwner =
                                                                    isTrueValue(event?.owner);
                                                                this.setState({
                                                                    role: isSelectedOwner,
                                                                    studentId: {
                                                                        ...event,
                                                                        value: event.id,
                                                                    },
                                                                });
                                                            }}
                                                            options={excludeSelectedStudentOptions(
                                                                this.state.isRescheduleMode
                                                                    ? this.state.studentList
                                                                    : this.state.timeApp &&
                                                                        this.state.timeApp.length
                                                                      ? this.state.studentList
                                                                      : [],
                                                                this.state.studentId
                                                            )}
                                                            getOptionValue={(option) =>
                                                                getStudentOptionKey(option)
                                                            }
                                                            hideSelectedOptions={true}
                                                            styles={participantSelectStyles}
                                                            isSearchable={true}
                                                            placeholder={this.props.t(
                                                                'searchTeamMemberName'
                                                            )}
                                                            noOptionsMessage={() =>
                                                                this.props.t('noOptions')
                                                            }
                                                            onKeyDown={(event) => {
                                                                const key = event.key;
                                                                const allowedCharacters =
                                                                    /^[a-zA-Z0-9\s]+$/;
                                                                if (
                                                                    !allowedCharacters.test(key) ||
                                                                    (event.keyCode === 32 &&
                                                                        !this.selectRef.state
                                                                            .inputValue)
                                                                ) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                            value={this.state.studentId}
                                                            formatOptionLabel={
                                                                this.customOptionLabel
                                                            }
                                                        />
                                                    ) : (
                                                        // Group/Classroom multi-select
                                                        <Select
                                                            isLoading={this.state.isStudentLoading}
                                                            ref={(r) => (this.selectRef = r)}
                                                            className="block rounded text-secondary text-md border-black-10 whitespace-nowrap"
                                                            name="students"
                                                            onChange={(event, actionMeta) => {
                                                                const selectedItems =
                                                                    event && event.length > 0
                                                                        ? event
                                                                        : [];
                                                                const isRemovalAction = [
                                                                    'remove-value',
                                                                    'pop-value',
                                                                    'clear',
                                                                    'deselect-option',
                                                                ].includes(actionMeta?.action);
                                                                this.setState((prevState) => ({
                                                                    studentIds:
                                                                        this.mergeLockedPrefilledStudents(
                                                                            selectedItems
                                                                        ),
                                                                    conferenceParticipantRemovalTouched:
                                                                        prevState.conference
                                                                            ? prevState.conferenceParticipantRemovalTouched ||
                                                                              isRemovalAction
                                                                            : false,
                                                                }));
                                                            }}
                                                            options={excludeSelectedStudentOptions(
                                                                this.state.isRescheduleMode
                                                                    ? this.state.studentList
                                                                    : this.state.timeApp &&
                                                                        this.state.timeApp.length
                                                                      ? this.state.studentList
                                                                      : [],
                                                                this.state.studentIds
                                                            )}
                                                            isMulti={true}
                                                            getOptionValue={(option) =>
                                                                getStudentOptionKey(option)
                                                            }
                                                            hideSelectedOptions={true}
                                                            styles={participantSelectStyles}
                                                            isSearchable={true}
                                                            placeholder={this.props.t(
                                                                'searchTeamMembersName'
                                                            )}
                                                            noOptionsMessage={() =>
                                                                this.props.t('noOptions')
                                                            }
                                                            onKeyDown={(event) => {
                                                                const key = event.key;
                                                                const allowedCharacters =
                                                                    /^[a-zA-Z0-9\s]+$/;
                                                                if (
                                                                    !allowedCharacters.test(key) ||
                                                                    (event.keyCode === 32 &&
                                                                        !this.selectRef.state
                                                                            .inputValue)
                                                                ) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                            value={
                                                                this.state.studentIds
                                                                    ? dedupeStudentOptions(
                                                                          this.state.studentIds
                                                                      )
                                                                    : []
                                                            }
                                                            isOptionDisabled={() =>
                                                                this.state.studentIds &&
                                                                this.state.studentIds.length >= 30
                                                            }
                                                            isDisabled={
                                                                this.state.isFromStudent
                                                                    ? true
                                                                    : false
                                                            } // optional for "fromStudent"
                                                            formatOptionLabel={
                                                                this.customOptionLabel
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {isConferenceSession && (
                                            <div className="grid grid-cols-1 mt-2">
                                                <label
                                                    htmlFor="email"
                                                    className="block mb-2 text-base font-normal text-white mt-6 after:content-['*'] after:ml-0.5 after:text-[#10DACE]"
                                                >
                                                    Enter Email's
                                                </label>

                                                <div className="flex items-center mb-2">
                                                    <input
                                                        type="text"
                                                        value={currentEmail}
                                                        onChange={this.handleInputChange}
                                                        placeholder={this.props.t(
                                                            'enterAnEmailAddress'
                                                        )}
                                                        className="w-full border border-black-10 bg-dateselect text-base text-white rounded py-1.5 px-2"
                                                        maxLength={100}
                                                        onKeyDown={(e) =>
                                                            e.key === 'Enter' &&
                                                            this.handleAddEmail()
                                                        }
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={this.handleAddEmail}
                                                        className="flex items-center justify-center w-6 h-6 ml-2 rounded-full bg-UIprimary sm:w-8 sm:h-8"
                                                    >
                                                        <i
                                                            className="text-white fas fa-plus"
                                                            style={{ fontSize: '11px' }}
                                                        ></i>
                                                    </button>
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {emails.map((email, index) => {
                                                        const isLockedPrefilledEmail =
                                                            isPrefilledValueLocked &&
                                                            this.isLockedPrefilledEmail(email);

                                                        return (
                                                            <div
                                                                key={index}
                                                                className={`flex items-center px-2 py-1 text-sm rounded-full ${
                                                                    isLockedPrefilledEmail
                                                                        ? 'bg-gray-600 text-gray-300'
                                                                        : 'bg-dateselect text-white'
                                                                }`}
                                                            >
                                                                {email}
                                                                <button
                                                                    type="button"
                                                                    disabled={
                                                                        isLockedPrefilledEmail
                                                                    }
                                                                    onClick={() =>
                                                                        this.handleRemoveEmail(
                                                                            index
                                                                        )
                                                                    }
                                                                    className={`ml-2 ${
                                                                        isLockedPrefilledEmail
                                                                            ? 'text-gray-400 cursor-not-allowed'
                                                                            : 'text-red-600 hover:text-red-800'
                                                                    }`}
                                                                >
                                                                    &times;
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {!isConferenceSession && (
                                            <>
                                                <div className="grid grid-cols-1 mt-6">
                                                    <div>
                                                        <label
                                                            htmlFor="select-about"
                                                            className="block mb-2 text-base font-normal leading-6 text-white"
                                                        >
                                                            {this.props.t('additionalSummary')}
                                                        </label>
                                                        <textarea
                                                            id="about"
                                                            name="about"
                                                            rows="3"
                                                            maxLength="300"
                                                            onChange={(event) => {
                                                                this.setState({
                                                                    summary: event.target.value,
                                                                });
                                                            }}
                                                            value={this.state.summary}
                                                            placeholder={this.props.t(
                                                                'summaryPlaceholder'
                                                            )}
                                                            className="block w-full p-2 text-white rounded bg-dateselect placeholder:text-[#B9B9B9] sm:text-base focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid mt-6 sm:grid-cols-2">
                                                    <div className="">
                                                        <label className="block text-base font-normal leading-6 text-white">
                                                            {this.props.t('uploadFiles')}
                                                        </label>
                                                        <div className="flex justify-center mt-2 rounded bg-dateselect">
                                                            <div className="mt-6 text-center">
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    id="file-upload"
                                                                    name="file-upload"
                                                                    className="sr-only bg-dateselect"
                                                                    onClick={(e) => {
                                                                        e.target.value = null;
                                                                    }}
                                                                    disabled={
                                                                        this.state.isImageUploaded
                                                                    }
                                                                    onChange={this.handleFileUpload}
                                                                    accept=".png,.jpeg,.jpg,.pdf"
                                                                />
                                                                <label
                                                                    htmlFor="file-upload"
                                                                    className="relative flex items-center justify-center p-8 rounded cursor-pointer"
                                                                >
                                                                    <span className="text-white">
                                                                        <div className="w-12 h-12 bg-[#543CC2] rounded-full flex items-center justify-center p-[14px] mx-auto">
                                                                            <svg
                                                                                width="20"
                                                                                height="20"
                                                                                viewBox="0 0 20 20"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    d="M5 10H15"
                                                                                    stroke="white"
                                                                                    strokeWidth="1.5"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                />
                                                                                <path
                                                                                    d="M10 15V5"
                                                                                    stroke="white"
                                                                                    strokeWidth="1.5"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                />
                                                                            </svg>{' '}
                                                                        </div>
                                                                        <div className="flex mt-4 text-sm leading-6 text-muted">
                                                                            <p className="pl-1 text-sm font-normal text-muted">
                                                                                {this.props.t(
                                                                                    'uploadInstructions'
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {this.state.uploadedImage &&
                                                this.state.uploadedImage.length ? (
                                                    <div className="flex flex-row flex-wrap w-auto gap-3 mt-4 bg-transparent rounded-lg max-w-fit min-width-50">
                                                        {this.state.uploadedImage.map(
                                                            (res, index) => {
                                                                const fileSrc =
                                                                    getReferenceFileSource(res);
                                                                const mimeType = String(
                                                                    (typeof res === 'object' &&
                                                                        (res?.type ||
                                                                            res?.mimeType)) ||
                                                                        ''
                                                                ).toLowerCase();
                                                                const isPdfFile = isPdfFileSource(
                                                                    fileSrc,
                                                                    mimeType
                                                                );
                                                                const isImageFile =
                                                                    isImageFileSource(
                                                                        fileSrc,
                                                                        mimeType
                                                                    );
                                                                return (
                                                                    <div
                                                                        className="relative upload"
                                                                        key={index}
                                                                    >
                                                                        <span
                                                                            className="cursor-pointer fas fa-times-circle text-red"
                                                                            style={{
                                                                                position:
                                                                                    'absolute',
                                                                                right: '-5px',
                                                                                top: '-5px',
                                                                            }}
                                                                            onClick={() => {
                                                                                this.removeObject(
                                                                                    index,
                                                                                    'image'
                                                                                );
                                                                            }}
                                                                        ></span>
                                                                        <div className="rounded upload-img border border-[#34373E]">
                                                                            {isPdfFile ? (
                                                                                <div>
                                                                                    <i
                                                                                        className="fa-solid fa-file-pdf"
                                                                                        style={{
                                                                                            color: '#c30909',
                                                                                            width: '5rem',
                                                                                            height: '5rem',
                                                                                            borderRadius:
                                                                                                '0.5rem',
                                                                                            fontSize:
                                                                                                '50px',
                                                                                            display:
                                                                                                'flex',
                                                                                            justifyContent:
                                                                                                'center',
                                                                                            alignItems:
                                                                                                'center',
                                                                                        }}
                                                                                    ></i>
                                                                                </div>
                                                                            ) : isImageFile ? (
                                                                                <img
                                                                                    src={fileSrc}
                                                                                    className="w-20 h-20 rounded"
                                                                                    key={index}
                                                                                    alt=""
                                                                                />
                                                                            ) : (
                                                                                <div>
                                                                                    <i
                                                                                        className="fa-solid fa-file"
                                                                                        style={{
                                                                                            color: '#9CA3AF',
                                                                                            width: '5rem',
                                                                                            height: '5rem',
                                                                                            borderRadius:
                                                                                                '0.5rem',
                                                                                            fontSize:
                                                                                                '50px',
                                                                                            display:
                                                                                                'flex',
                                                                                            justifyContent:
                                                                                                'center',
                                                                                            alignItems:
                                                                                                'center',
                                                                                        }}
                                                                                    ></i>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-end justify-end mt-6 sm:mt-auto">
                                    <button
                                        type="button"
                                        className="sm:w-1/6 w-full text-white font-semibold bg-black px-3 py-1.5 rounded-lg"
                                        onClick={() =>
                                            this.props.history.push(
                                                `${PATH.IM_PHYSICIAN.APPOINTMENT}/${this.props.match.params.userId}/`
                                            )
                                        }
                                    >
                                        {this.props.t('cancel')}
                                    </button>
                                    <button
                                        onClick={() => this.handleSubmit()}
                                        disabled={this.state.isLoading}
                                        className="rounded-lg bg-UIprimary px-3 py-1.5 text-base font-semibold text-white sm:w-1/6 w-full ml-4"
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {this.state.isLoading ? (
                                                <Spinner color="#FFF" />
                                            ) : isRescheduleMode ? (
                                                this.props.t('update')
                                            ) : (
                                                this.props.t('create')
                                            )}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        subCategoryList: state.patientAppointmentState.subCategoryList,
        userDetails: state.UserReducer.userDetails,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getEducatorAvailablityDays: (educatorId, callback) => {
            dispatch(getEducatorAvailablityDays(educatorId, callback));
        },
        getEducatorSubject: (educatorId, values, callback) => {
            dispatch(getEducatorSubject(educatorId, values, callback));
        },
        getEducatorSubCategoryList: (subjectId, values, callBack) => {
            dispatch(getEducatorSubCategoryList(subjectId, values, callBack));
        },
        getPhysicianAvailability: (id, date, callBack) => {
            dispatch(getPhysicianAvailability(id, date, callBack));
        },
        getEducatorAvailability: (id, date, callBack, options) => {
            dispatch(getEducatorAvailability(id, date, callBack, options));
        },
        getAvailableStudentList: (values, callBack) => {
            dispatch(getAvailableStudentList(values, callBack));
        },
        getVisitInformation: (visitId, callBack) => {
            dispatch(getVisitInformation(visitId, callBack));
        },
        appointmentBook: (values, callBack) => {
            dispatch(appointmentBook(values, callBack));
        },
        rescheduleSession: (values, callBack) => {
            dispatch(rescheduleSession(values, callBack));
        },
        createClassRoomSession: (values, callBack) => {
            dispatch(createClassRoomSession(values, callBack));
        },
        showNotification: (values, callback) => {
            dispatch(showNotification(values, callback));
        },
    };
};

const ConnectedCreateSession = withLanguage(
    connect(mapStateToProps, mapDispatchToProps)(CreateSession)
);

export default (props) => (
    <LanguageProvider>
        <ConnectedCreateSession {...props} />
    </LanguageProvider>
);
